import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useApp } from '../context/AppContext';
import { useLanguage } from '../i18n/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { bookingErrorKey, classifyBookingError } from '../utils/rideErrors';
import { retryWhenRateLimited } from '../utils/rideQuote';
import { buildTelUrl } from '../utils/support';
import { AUTO_SEATS, selectedTip, pickupMessage, dropTimeLabel, pickupInfoIsStale } from '../utils/pickupInfo';
import { pickupNeedsCheck, pickupCheckOutcome } from '../utils/pickupCheck';
import { riderNoteSummary } from '../utils/riderNote';
import { waitingTermsFromQuote } from '../utils/waitingCharge';
import { intertownCanTakeOutsideDrop, intertownDropRedirect, intertownHandover } from '../utils/intertownRedirect';
import { createRide, findActiveRideId, getRideQuote, getUserRideStats } from '../services/firebase';
import { APP, clearSelectedTownId, readSelectedTownId, saveSelectedTownId } from '../config/app';
import {
  createMap, mountPlaceAutocomplete, getDirections, drawRoute, placeMarker, placeSavedPlaceMarker,
  getDevicePosition, getAccuratePosition, getBestDevicePosition, getLastKnownPosition, refinePosition, reverseGeocode,
  formatPlaceLabel,
  isBillingError, isWithinServiceArea, applyDetectedZone, applyZoneById, getServiceZones,
  getGeoErrorKey, fitMapToPoints, fitMapToPoint, clearMarkers, applyMapSheetPadding, getMapCenterLatLng,
  distanceBetweenM, bindPinnedCenterPin, getPointUnderVisualPin, createUserAccuracyCircle, onceMapIdle,
  destroyMap,
  NIGHT_MAP_STYLE, DAY_MAP_STYLE,
} from '../services/maps';
import NotificationPrompt from '../components/NotificationPrompt';
import LangToggle from '../components/LangToggle';
import FirstTimeTour from '../components/FirstTimeTour';
import MapRecenterIcon from '../components/MapRecenterIcon';
import BookForSomeoneSheet from '../components/BookForSomeoneSheet';
import EmergencyContactsPrompt from '../components/EmergencyContactsPrompt';
import TownSelector from '../components/TownSelector';
import Splash from './Splash';
import PinSheetAction from '../components/PinSheetAction';
import RiderNoteSheet from '../components/RiderNoteSheet';
import { hapticSelection, hapticSuccess } from '../utils/haptics';
import { warmChime } from '../utils/chime';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import { registerAndroidBackHandler } from '../utils/androidBack';
import {
  showLocationDeniedToast,
  showLocationDisabledToast,
  checkNativeLocationStatus,
} from '../utils/locationPermission';
import { isNativeApp } from '../utils/platform';
import { hasQueuedPlayReview, requestQueuedPlayReview } from '../utils/playReview';
import { App as CapApp } from '@capacitor/app';
import { onZoneChange } from '../services/zoneEvents';

const LayersMapIcon = () => (
  <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m12 3 9 5-9 5-9-5 9-5Z" />
    <path d="m3 12 9 5 9-5" />
    <path d="m3 16 9 5 9-5" />
  </svg>
);

const TownSwitcherChevron = () => (
  <span className="rapido-city-switcher-caret" aria-hidden>
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="m7 10 5 5 5-5" />
    </svg>
  </span>
);

const cachePlaceLocal = (key, place) => {
  if (!place?.lat) return;
  try {
    localStorage.setItem(key, JSON.stringify({
      name: place.name || place.address,
      address: place.address || place.name,
      lat: place.lat,
      lng: place.lng,
    }));
  } catch { /* */ }
};

const FAR_PICKUP_KM = 1;
// Longest Book waits on the splash for the town list before opening on a
// saved town anyway (see the startup effect).
const STARTUP_CONFIG_WAIT_MS = 6000;
// A new GPS fix this close to the address already shown keeps that address
// until its own lookup returns, instead of flashing raw coordinates.
const GPS_LABEL_KEEP_M = 30;
const OUT_OF_TOWN_ASKED_KEY = 'mtl_rides_oot_asked_v1';

const evalBookForOther = (userGps, pickup) => {
  if (!userGps?.lat || !userGps?.lng || !pickup?.lat || !pickup?.lng) {
    return { prompt: false };
  }
  if (!isWithinServiceArea(pickup.lat, pickup.lng)) {
    return { prompt: false };
  }
  const distKm = distanceBetweenM(userGps, pickup) / 1000;
  if (!isWithinServiceArea(userGps.lat, userGps.lng)) {
    return { prompt: true, reason: 'outOfTown', distanceKm: distKm };
  }
  if (distKm > FAR_PICKUP_KM) {
    return { prompt: true, reason: 'farPickup', distanceKm: distKm };
  }
  return { prompt: false, distanceKm: distKm };
};

const wasOutOfTownAsked = () => {
  try {
    return sessionStorage.getItem(OUT_OF_TOWN_ASKED_KEY) === '1';
  } catch {
    return false;
  }
};

const markOutOfTownAsked = () => {
  try {
    sessionStorage.setItem(OUT_OF_TOWN_ASKED_KEY, '1');
  } catch { /* */ }
};

const HomeChipIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
    <polyline points="9 22 9 12 15 12 15 22"/>
  </svg>
);

const WorkChipIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
  </svg>
);

const BusChipIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 16v1a2 2 0 0 0 2 2h1v1a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1v-1h4v1a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1v-1h1a2 2 0 0 0 2-2v-1M4 16V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v10M4 16h16M7 10h.01M17 10h.01M7 6h10"/>
  </svg>
);

const RailChipIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="4" y="3" width="16" height="13" rx="2"/>
    <path d="M4 11h16M12 3v8M8 19l-3 3M16 19l3 3M8 16h8"/>
    <circle cx="8" cy="14" r="1"/>
    <circle cx="16" cy="14" r="1"/>
  </svg>
);

const HospChipIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 6v12M6 12h12"/>
    <rect x="3" y="3" width="18" height="18" rx="4"/>
  </svg>
);

const CrossroadChipIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="3"/>
    <path d="M12 2v7M12 15v7M2 12h7M15 12h7"/>
  </svg>
);

const MarketChipIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
    <line x1="3" y1="6" x2="21" y2="6"/>
    <path d="M16 10a4 4 0 0 1-8 0"/>
  </svg>
);

const PlusChipIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="12" y1="5" x2="12" y2="19"/>
    <line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);

const getTimeGreeting = (t) => {
  const hour = new Date().getHours();
  if (hour < 12) return t('book.greetMorning');
  if (hour < 17) return t('book.greetAfternoon');
  return t('book.greetEvening');
};

const PRESET_SHORTCUTS = [
  { id: 'bus', Icon: BusChipIcon, key: 'shortcutBusStand', label: 'Bus Stand', place: { name: 'APSRTC Bus Stand, Pileru', address: 'APSRTC Bus Stand, Pileru, Andhra Pradesh', lat: 13.6547389, lng: 78.9445406 } },
  { id: 'crossroad', Icon: CrossroadChipIcon, key: 'shortcutCrossRoad', label: 'Cross Road', place: { name: 'Piler Cross Road', address: 'Piler Cross Road, Pileru, Andhra Pradesh', lat: 13.654869, lng: 78.9471378 } },
  { id: 'market', Icon: MarketChipIcon, key: 'shortcutMarketYard', label: 'Market Yard', place: { name: 'Market Yard Complex', address: 'Market Yard Complex, Pileru, Andhra Pradesh', lat: 13.6563587, lng: 78.9454413 } },
  { id: 'rail', Icon: RailChipIcon, key: 'shortcutRailway', label: 'Railway Station', place: { name: 'Pileru Railway Station', address: 'Pileru Railway Station, Andhra Pradesh', lat: 13.6625162, lng: 78.9280908 } },
  { id: 'hosp', Icon: HospChipIcon, key: 'shortcutHospital', label: 'Govt. Hospital', place: { name: 'Govt. Hospital Pileru', address: 'Govt. Hospital Pileru, Andhra Pradesh', lat: 13.654472, lng: 78.9418376 } },
];

const truncate = (s, n = 42) => (s && s.length > n ? `${s.slice(0, n)}…` : s || '');

const PICKUP_CACHE_KEY = 'mtl_rides_pickup_v1';
// localStorage (survives an app kill, unlike sessionStorage) with a short TTL —
// long enough to make "reopen and rebook" instant, short enough that a stale
// pin never becomes the confirmed pickup (detectPickup still refines on every open).
const PICKUP_CACHE_MAX_AGE_MS = 2 * 60 * 60 * 1000;

const loadPickupCache = () => {
  try {
    const raw = localStorage.getItem(PICKUP_CACHE_KEY);
    if (!raw) return null;
    const place = JSON.parse(raw);
    if (!place?.lat || !place?.lng) return null;
    if (Date.now() - (place.cachedAt || 0) > PICKUP_CACHE_MAX_AGE_MS) return null;
    return place;
  } catch {
    return null;
  }
};

const savePickupCache = (place) => {
  if (!place?.lat || !place?.lng) return;
  try {
    localStorage.setItem(PICKUP_CACHE_KEY, JSON.stringify({ ...place, cachedAt: Date.now() }));
  } catch {
    /* storage full / private mode */
  }
};

// Nearest-auto info older than this is re-fetched while the confirm screen is open.
const PICKUP_INFO_MAX_AGE_MS = 30 * 1000;
const PICKUP_INFO_CHECK_MS = 10 * 1000;

/** Cache key for one pickup -> drop route (the quote is fetched once per key). */
const routeKeyFor = (from, to) => (from?.lat && to?.lat
  ? `${from.lat.toFixed(5)},${from.lng.toFixed(5)}->${to.lat.toFixed(5)},${to.lng.toFixed(5)}`
  : null);

export default function Book({ isTabActive = true, onChromeHiddenChange }) {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    user, userProfile, activeRide, activeIntertownRequest, rides, ridesLoaded, platformMode, platformMessage,
    platformConfigReady, platformConfigError, retryPlatformConfig, setBookingFlowActive, platform,
  } = useApp();
  const bookingHalted = platformMode === 'paused' || platformMode === 'emergency';
  const { t, lang } = useLanguage();
  const online = useNetworkStatus();
  const { isDark } = useTheme();
  // Only brand-new accounts get tourDone: false from ensureUserDefaults.
  const [showTour, setShowTour] = useState(false);

  const mapRef = useRef(null);
  const sheetRef = useRef(null);
  const dropAcRef = useRef(null);
  const pickupAcRef = useRef(null);
  const mapInstance = useRef(null);
  const routeLine = useRef(null);
  const markers = useRef([]);
  const savedPlaceMarkers = useRef([]);
  const mapInitDone = useRef(false);
  const [mapReady, setMapReady] = useState(false);
  const [townReady, setTownReady] = useState(false);
  const [showTownSwitcher, setShowTownSwitcher] = useState(false);
  const [zoneRevision, setZoneRevision] = useState(0);
  const startupTownAttemptRef = useRef(false);
  const startupLocationAskedRef = useRef(false);
  // Safety valve for the ride-restore wait below: listenToUserRides has no
  // error callback wired up in AppContext, so a failed/stuck rides listener
  // (e.g. cold open with no cache and a bad connection) would otherwise leave
  // ridesLoaded false forever and strand the passenger on the loading gate
  // with no town picker and no way out. After a few seconds, give up waiting
  // and proceed as if the ride check came back empty.
  const [ridesRestoreTimedOut, setRidesRestoreTimedOut] = useState(false);
  const [configWaitTimedOut, setConfigWaitTimedOut] = useState(false);
  const [townRetrying, setTownRetrying] = useState(false);
  // A GPS pickup is on screen but its street address is still being looked up.
  const [pickupAddressPending, setPickupAddressPending] = useState(false);
  // Rider said "No thanks" to turning location on at startup: a remembered
  // pickup may still show, so say plainly that location is off.
  const [startupLocationOff, setStartupLocationOff] = useState(false);

  const [step, setStep] = useState('home'); // home | search | confirm | pin
  // The bottom nav is owned by App (one persistent instance so the glider slides
  // between tabs). Book only tells App to hide it while the booking sheet is
  // expanded (search / confirm / pin).
  useEffect(() => {
    onChromeHiddenChange?.(step !== 'home' || !townReady);
  }, [step, townReady, onChromeHiddenChange]);
  const [pickup, setPickup] = useState(null);
  const [drop, setDrop] = useState(null);
  const [routeInfo, setRouteInfo] = useState(null);
  // Book tapped with a GPS pickup: the pin step is the "Is this your pickup spot?"
  // check (utils/pickupCheck.js). Holds the pickup as it was when Book was tapped.
  const [pickupCheck, setPickupCheck] = useState(null);
  // The check moved the pickup: { fare before the move, route key of the new route },
  // for the "fare changed" note. Shown only once that new route's quote is in.
  const [movedFromFare, setMovedFromFare] = useState(null);
  // Optional note for the Anna (utils/riderNote.js), kept until this trip is booked or dropped.
  const [riderNote, setRiderNote] = useState(null);
  const [noteSheetOpen, setNoteSheetOpen] = useState(false);
  // Optional pickup tip the rider picked on the confirm screen (0 = none).
  const [tipChoice, setTipChoice] = useState(0);
  const [booking, setBooking] = useState(false);
  // A mandatory update must never interrupt a rider while they are choosing
  // pickup/drop or while the atomic create-ride request is in flight.
  useEffect(() => {
    setBookingFlowActive?.(step !== 'home' || booking);
    return () => setBookingFlowActive?.(false);
  }, [step, booking, setBookingFlowActive]);
  // `booking` is async state — two taps in the same frame both see it false and
  // both fire createRide (→ two concurrent rides). This ref flips synchronously.
  const bookingLockRef = useRef(false);
  const [locating, setLocating] = useState(true);
  const [locationError, setLocationError] = useState('');
  const [editingPickup, setEditingPickup] = useState(false);
  const [mapError, setMapError] = useState('');
  const [mapType, setMapType] = useState('roadmap');

  const toggleMapType = () => {
    if (!mapInstance.current) return;
    const nextType = mapType === 'roadmap' ? 'hybrid' : 'roadmap';
    mapInstance.current.setMapTypeId(nextType);
    setMapType(nextType);
  };

  useEffect(() => {
    if (!mapInstance.current || mapType === 'hybrid') return;
    mapInstance.current.setOptions({
      styles: DAY_MAP_STYLE,
    });
  }, [mapType]);
  const [routeLoading, setRouteLoading] = useState(false);
  const [pickupValid, setPickupValid] = useState(true);
  const [pinTarget, setPinTarget] = useState(null); // 'pickup' | 'drop'
  // Name of the colony/area the rider searched, while they pin the exact drop in it.
  const [pinAreaName, setPinAreaName] = useState(null);
  const openPinPickerRef = useRef(null);
  const [pinPlace, setPinPlace] = useState(null);
  const [pinLoading, setPinLoading] = useState(false);
  const [pinValid, setPinValid] = useState(true);
  // A drop outside the service area can be booked as an Intertown trip instead, but only when
  // that would really work (utils/intertownRedirect.js). Never offered for a pickup.
  const [redirecting, setRedirecting] = useState(false);
  const dropRedirect = useMemo(() => (
    step === 'pin' && pinTarget === 'drop' && !pinValid && pinPlace
      ? intertownDropRedirect({ platform, pickup, drop: pinPlace })
      : null
  ), [step, pinTarget, pinValid, pinPlace, platform, pickup]);
  const [userGps, setUserGps] = useState(null);
  const [confirmMinimized, setConfirmMinimized] = useState(false);
  const touchStartY = useRef(null);

  const handleSheetTouchStart = (e) => {
    touchStartY.current = e.touches[0]?.clientY;
  };
  const handleSheetTouchEnd = (e) => {
    if (touchStartY.current == null) return;
    const deltaY = (e.changedTouches[0]?.clientY || 0) - touchStartY.current;
    if (deltaY > 35) {
      setConfirmMinimized(true);
    } else if (deltaY < -35) {
      setConfirmMinimized(false);
    }
    touchStartY.current = null;
  };

  useEffect(() => {
    if (step !== 'confirm') {
      setConfirmMinimized(false);
    }
    if (step !== 'confirm' && step !== 'pin') setMovedFromFare(null);
    // However the pin step was left, the check is over: a later "Adjust on map"
    // must never book on Confirm.
    if (step !== 'pin') setPickupCheck(null);
  }, [step]);
  const [forOtherSheet, setForOtherSheet] = useState(null); // { reason, distanceKm } | null
  const [notifSheetActive, setNotifSheetActive] = useState(false);
  const [notifReady, setNotifReady] = useState(false);
  const [forOtherDecision, setForOtherDecision] = useState(null); // null | 'self' | 'other'
  const [riderDetails, setRiderDetails] = useState(null); // { riderName, riderPhone }
  const pinReturnStep = useRef('search');
  const stepRef = useRef(step);
  const pickupRef = useRef(pickup);
  const dropRef = useRef(drop);
  const manualPickupSelectedRef = useRef(false);
  const lastGpsPlaceRef = useRef(null);
  const pendingSaveShortcutRef = useRef(null);
  const forOtherKeyRef = useRef('');

  useEffect(() => {
    stepRef.current = step;
  }, [step]);

  useEffect(() => {
    pickupRef.current = pickup;
  }, [pickup]);

  useEffect(() => {
    dropRef.current = drop;
  }, [drop]);

  useEffect(() => {
    if (userProfile?.tourDone === false) setShowTour(true);
    else if (userProfile?.tourDone === true) setShowTour(false);
  }, [userProfile?.tourDone]);
  const pinGeocodeSeq = useRef(0);
  const gpsApplySeq = useRef(0);
  const refineStopRef = useRef(null);
  const mapCenterTargetRef = useRef(null);
  const userPannedMapRef = useRef(false);
  const programmaticMapMoveRef = useRef(false);
  const accuracyCircleRef = useRef(null);

  useEffect(() => {
    if (step !== 'home' || !mapInstance.current || !userGps?.lat) {
      if (accuracyCircleRef.current) {
        accuracyCircleRef.current.destroy();
        accuracyCircleRef.current = null;
      }
      return;
    }

    if (!accuracyCircleRef.current) {
      accuracyCircleRef.current = createUserAccuracyCircle(
        mapInstance.current,
        userGps,
        userGps.accuracy,
      );
    } else {
      accuracyCircleRef.current.update(userGps, userGps.accuracy);
    }

    return () => {
      if (accuracyCircleRef.current) {
        accuracyCircleRef.current.destroy();
        accuracyCircleRef.current = null;
      }
    };
  }, [step, userGps]);

  const readSafePx = (prop) => {
    if (typeof window === 'undefined') return 0;
    const n = Number.parseInt(
      getComputedStyle(document.documentElement).getPropertyValue(prop),
      10,
    );
    return Number.isFinite(n) ? n : 0;
  };

  const getMapPadding = useCallback(() => {
    const sheetH = sheetRef.current?.getBoundingClientRect().height || 360;
    const navH = step === 'home' ? 58 : 0;
    const safeBottom = readSafePx('--safe-area-bottom');
    const safeTop = readSafePx('--safe-area-top');
    return { top: 56 + safeTop, bottom: sheetH + navH + safeBottom, left: 24, right: 24 };
  }, [step]);

  const handlePlaceSelect = useCallback((setter, onDone) => (place, err) => {
    if (err === 'outsideArea') {
      toast.error(t('book.outsideArea', { km: APP.serviceRadiusKm, city: APP.city }));
      return;
    }
    if (err === 'billingError') {
      toast.error(t('book.billingError'));
      return;
    }
    if (err === 'placeError') {
      toast.error(t('book.placeError'));
      return;
    }
    if (!place) return;
    hapticSelection();
    const resolved = { ...place, source: 'search' };
    setter(resolved);
    if (setter === setPickup) {
      refineStopRef.current?.();
      refineStopRef.current = null;
      manualPickupSelectedRef.current = true;
      setEditingPickup(false);
      setPickupValid(true);
      savePickupCache(resolved);
    }
    onDone?.(resolved);
  }, [t]);

  const handleDropSelected = useCallback((place, err, meta) => {
    userPannedMapRef.current = false;
    // A colony, nagar, area or road comes back as one label point for the
    // whole area, so a fare priced to it falls short when the rider actually
    // goes deep inside. Have them pin the exact drop first; the drop is only
    // set when the pin is confirmed, so backing out can't book the label point.
    if (!err && place && meta?.isArea && !pendingSaveShortcutRef.current) {
      hapticSelection();
      openPinPickerRef.current?.('drop', { ...place, source: 'search' });
      return;
    }
    // A searched place outside the service area used to end in an error toast. If it can be
    // booked as an Intertown trip, show it on the pin map instead: the red "Outside service
    // area" card is there, with the Intertown button in place of the disabled Confirm.
    if (err === 'outsideArea' && meta?.place && !pendingSaveShortcutRef.current
      && intertownDropRedirect({ platform, pickup, drop: meta.place }).ok) {
      hapticSelection();
      openPinPickerRef.current?.('drop', { ...meta.place, source: 'search' }, { plain: true });
      return;
    }
    handlePlaceSelect(setDrop, (selectedPlace) => {
      if (pendingSaveShortcutRef.current) {
        const shortcutId = pendingSaveShortcutRef.current;
        pendingSaveShortcutRef.current = null;
        cachePlaceLocal(shortcutId === 'home' ? 'mtl_saved_home' : 'mtl_saved_work', selectedPlace);
        toast.success(shortcutId === 'home' ? (t('book.savedHome') || 'Saved as Home') : (t('book.savedWork') || 'Saved as Work'));
      }
      // Instant 0ms Optimistic Fare Calculation (smooth Rapido/Uber instant card):
      if (pickup?.lat && selectedPlace?.lat) {
        const straightMeters = distanceBetweenM(pickup, selectedPlace);
        const estKm = Math.max(0.3, Math.round(((straightMeters / 1000) * 1.25) * 10) / 10);
        const estFare = Math.max(APP.fare.minimum, Math.round(APP.fare.base + estKm * APP.fare.perKm));
        const estMin = Math.max(2, Math.round(estKm * 2.8));
        setRouteInfo({
          distanceKm: estKm,
          durationMin: estMin,
          fare: estFare,
          isEstimating: true,
          isServerQuote: false,
        });
      }
      setStep('confirm');
    })(place, err);
  }, [handlePlaceSelect, t, pickup, platform]);

  const resolveSavedDrop = (kind) => {
    const fromProfile = kind === 'home'
      ? userProfile?.savedPlaces?.home
      : userProfile?.savedPlaces?.work;
    if (fromProfile?.lat && fromProfile?.lng) return fromProfile;
    try {
      const raw = localStorage.getItem(kind === 'home' ? 'mtl_saved_home' : 'mtl_saved_work');
      if (!raw) return null;
      const place = JSON.parse(raw);
      return place?.lat ? place : null;
    } catch {
      return null;
    }
  };

  const handleShortcutClick = (shortcut) => {
    hapticSelection();
    if (shortcut.id === 'home' || shortcut.id === 'work') {
      const place = resolveSavedDrop(shortcut.id);
      if (place) {
        cachePlaceLocal(shortcut.id === 'home' ? 'mtl_saved_home' : 'mtl_saved_work', place);
        const resolved = { ...place, source: 'saved' };
        if (editingPickup) {
          setPickup(resolved);
          savePickupCache(resolved);
          refineStopRef.current?.();
          refineStopRef.current = null;
          manualPickupSelectedRef.current = true;
          setEditingPickup(false);
          setPickupValid(true);
          setStep('home');
          centerMapOnPickup(resolved);
          return;
        }
        if (!pickupValid || !pickup) {
          setStep('search');
          setEditingPickup(true);
          toast(t('book.setPickupFirst'), { icon: '📍' });
          setDrop(resolved);
          return;
        }
        handleDropSelected(resolved);
        return;
      }
      pendingSaveShortcutRef.current = shortcut.id;
      openPinPicker('drop');
      toast(shortcut.id === 'home' ? (t('book.saveHomeTitle') || 'Save Home Address') : (t('book.saveWorkTitle') || 'Save Work Address'), {
        icon: shortcut.id === 'home' ? '🏠' : '💼',
      });
      return;
    }
    const resolved = { ...shortcut.place, source: 'saved' };
    if (editingPickup) {
      setPickup(resolved);
      savePickupCache(resolved);
      refineStopRef.current?.();
      refineStopRef.current = null;
      manualPickupSelectedRef.current = true;
      setEditingPickup(false);
      setPickupValid(true);
      setStep('home');
      centerMapOnPickup(resolved);
      return;
    }
    if (!pickupValid || !pickup) {
      setStep('search');
      setEditingPickup(true);
      toast(t('book.setPickupFirst'), { icon: '📍' });
      setDrop(resolved);
      return;
    }
    handleDropSelected(resolved);
  };

  const shortcutClickRef = useRef(handleShortcutClick);
  shortcutClickRef.current = handleShortcutClick;

  // Show the saved Home / Work pins on the booking map so the user can see
  // where they are (and tap to pick). Only on the home step, before a route.
  const savedHome = resolveSavedDrop('home');
  const savedWork = resolveSavedDrop('work');
  const savedHomeKey = savedHome ? `${savedHome.lat},${savedHome.lng}` : '';
  const savedWorkKey = savedWork ? `${savedWork.lat},${savedWork.lng}` : '';
  useEffect(() => {
    const map = mapInstance.current;
    const clear = () => {
      savedPlaceMarkers.current.forEach((m) => { try { m.setMap(null); } catch { /* */ } });
      savedPlaceMarkers.current = [];
    };
    if (!map || !mapReady || step !== 'home' || drop) { clear(); return undefined; }
    clear();
    const onTap = (kind) => shortcutClickRef.current({ id: kind });
    if (savedHome?.lat) {
      const m = placeSavedPlaceMarker(map, savedHome, 'home', t('places.home'), onTap);
      if (m) savedPlaceMarkers.current.push(m);
    }
    if (savedWork?.lat) {
      const m = placeSavedPlaceMarker(map, savedWork, 'work', t('places.work'), onTap);
      if (m) savedPlaceMarkers.current.push(m);
    }
    return clear;
  }, [step, savedHomeKey, savedWorkKey, drop, mapReady, t]);

  const centerMapOnPickup = useCallback((pos) => {
    if (!pos?.lat || !pos?.lng) return;
    mapCenterTargetRef.current = { lat: pos.lat, lng: pos.lng };
    userPannedMapRef.current = false;
    const map = mapInstance.current;
    if (!map) return;
    programmaticMapMoveRef.current = true;
    const padding = getMapPadding();
    requestAnimationFrame(() => {
      fitMapToPoint(map, pos, padding);
      onceMapIdle(map, () => { programmaticMapMoveRef.current = false; });
    });
  }, [getMapPadding]);

  const applyPickupFromGps = useCallback(async (pos, approx, silent = false, { explicitZone = false } = {}) => {
    if (silent && (manualPickupSelectedRef.current || (stepRef.current && stepRef.current !== 'home'))) {
      setUserGps({ lat: pos.lat, lng: pos.lng, accuracy: pos.accuracy });
      return { zone: null, inArea: false };
    }
    // Resolve which town this fix is in (if any) BEFORE the in-area check, so
    // that check runs against the right zone's own center/radius — e.g. a
    // Kalikiri fix must be measured against Kalikiri's circle, not Piler's.
    const zone = applyDetectedZone(pos.lat, pos.lng, { explicit: explicitZone });
    const inArea = isWithinServiceArea(pos.lat, pos.lng);
    setStartupLocationOff(false); // a real fix: location is on after all

    setUserGps({ lat: pos.lat, lng: pos.lng, accuracy: pos.accuracy });

    if (!inArea) {
      // Booker outside every zone, or in one that exists but hasn't launched
      // yet — keep GPS for “for someone else”, don’t force invalid pickup.
      setLocationError('');
      if (!silent) {
        if (zone && !zone.active) {
          // A real, named zone (e.g. Kalikiri) with no live fleet yet — shown
          // to everyone, not just guests, so this is never a silent "no auto
          // found" failure once they do try to book.
          toast(t('book.zoneComingSoon', { city: zone.name }), { icon: '🛺', duration: 6000 });
        } else if (!user?.uid) {
          // Logged-in users get the early sheet; toast only for guests / silent=false fallbacks.
          toast(t('book.locationOutsideHint', { city: APP.city }), { icon: '📍', duration: 4500 });
        }
      }
      return { zone, inArea: false };
    }

    if (silent && (manualPickupSelectedRef.current || (stepRef.current && stepRef.current !== 'home'))) {
      return { zone, inArea: true };
    }

    // Guarded setter: GPS never overwrites a manual pick or a ride on the confirm step.
    const setPickupIfGps = (next) => setPickup((currentPickup) => {
      if (stepRef.current === 'confirm' || (currentPickup?.source && currentPickup.source !== 'gps')) {
        return currentPickup;
      }
      return next;
    });

    // 1) Drop the pin from coordinates immediately — never block the UI on the
    //    reverse-geocode network call.
    gpsApplySeq.current += 1;
    const seq = gpsApplySeq.current;
    const shown = pickupRef.current;
    const keepShownLabel = Boolean(
      shown?.source === 'gps' && shown.name && shown.address && shown.name !== t('book.pinApprox')
      && distanceBetweenM(shown, pos) <= GPS_LABEL_KEEP_M,
    );
    const coordPlace = keepShownLabel
      ? { ...shown, lat: pos.lat, lng: pos.lng, source: 'gps', isApproximate: approx, accuracy: pos.accuracy }
      : {
        lat: pos.lat,
        lng: pos.lng,
        source: 'gps',
        name: t('book.pinApprox'),
        address: `${pos.lat.toFixed(5)}, ${pos.lng.toFixed(5)}`,
        isApproximate: approx,
        accuracy: pos.accuracy,
      };
    setPickupAddressPending(!keepShownLabel);
    lastGpsPlaceRef.current = coordPlace;
    setPickupIfGps(coordPlace);
    setPickupValid(true);
    setLocationError('');
    savePickupCache(coordPlace);
    if (!silent || (!userPannedMapRef.current && stepRef.current === 'home')) {
      centerMapOnPickup(coordPlace);
    }
    if (approx && !silent) {
      toast(t('book.locationApproxHint'), { icon: '📍' });
    }

    // 2) Resolve the street name in the background and swap the label in — unless
    //    a newer fix has already superseded this one.
    reverseGeocode(pos.lat, pos.lng)
      .then((geocoded) => {
        if (gpsApplySeq.current !== seq) return;
        const labelled = { ...geocoded, source: 'gps', isApproximate: approx, accuracy: pos.accuracy };
        lastGpsPlaceRef.current = labelled;
        setPickupIfGps(labelled);
        savePickupCache(labelled);
        setPickupAddressPending(false);
      })
      .catch(() => {
        // Keep the coordinate label, and stop saying "Getting address…".
        if (gpsApplySeq.current === seq) setPickupAddressPending(false);
      });
    return { zone, inArea: true };
  }, [t, centerMapOnPickup, user?.uid]);

  const detectPickup = useCallback(async ({ userInitiated = false, startup = false } = {}) => {
    refineStopRef.current?.();
    refineStopRef.current = null;
    if (userInitiated) {
      manualPickupSelectedRef.current = false;
    }
    setLocationError('');
    setLocating(true);
    let landedFromQuick = false;
    let quickZone = null;
    let accurateApplied = false;
    try {
      if (!userInitiated || startup) {
        // Race a near-instant coarse fix (last-known / cell) against the accurate
        // stream — in parallel, so its 3s ceiling never delays the real fix. It
        // just lands the map on the right area in ~ms while the race runs.
        getLastKnownPosition()
          .then(async (quick) => {
            if (accurateApplied || !quick) return;
            if (manualPickupSelectedRef.current || stepRef.current !== 'home') return;
            // A launch-time GPS fix takes precedence over a town remembered
            // from a previous location-denied session. This coarse fix gets
            // the map into the correct town immediately while the accurate
            // fix continues in the background.
            const quickResult = await applyPickupFromGps(quick, (quick.accuracy ?? 99) > 45, true, {
              explicitZone: userInitiated,
            }); // silent: no "approximate" toast
            if (quickResult?.inArea && quickResult.zone?.active !== false) {
              quickZone = quickResult.zone;
              landedFromQuick = true;
              setLocating(false);
              // Do not hold the entire booking screen behind a slower GPS
              // refinement when we already have a valid recent fix. The
              // ongoing accurate read will still update the pin if it wins.
              if (startup) setTownReady(true);
            }
          })
          .catch(() => { /* no last-known fix — the accurate race still runs */ });
      }
      const pos = userInitiated
        ? await getBestDevicePosition({ requestPermission: true }).catch((err) => {
            if (err?.message === 'denied' || err?.message === 'location_disabled') throw err;
            // Cold opens must never leave the passenger on "Getting
            // location…" for another 15+ seconds after the fast race timed
            // out. The town fallback / chooser is the right next screen.
            if (startup) throw err;
            return getAccuratePosition();
          })
        : await getDevicePosition();
      accurateApplied = true;
      // Refine silently if the quick fix already placed a valid pin.
      const result = await applyPickupFromGps(
        pos,
        (pos.accuracy ?? 99) > 45,
        landedFromQuick,
        { explicitZone: userInitiated },
      );
      setLocating(false);
      // Remember the town GPS put the rider in. result.zone is set only when
      // the fix was allowed to choose the town (gpsMayChangeTown), so a town
      // the rider picked in this session is never overwritten here.
      if (result?.zone && result.zone.active !== false) {
        saveSelectedTownId(result.zone.id);
      }
      refineStopRef.current = refinePosition((refined) => {
        applyPickupFromGps(refined, (refined.accuracy ?? 99) > 45, true);
      });
      return result;
    } catch (e) {
      accurateApplied = true;
      // The quick fix already gave us a usable pin — swallow a later accurate-fix
      // failure rather than yanking the map back to town centre.
      if (landedFromQuick) {
        setLocating(false);
        // Persist the town here too, same as the accurate-fix success path
        // below — otherwise a slow/denied/timed-out accurate fix (common on
        // desktop browsers re-prompting for permission) means the town never
        // gets saved even though the quick fix correctly resolved it, and the
        // next refresh has nothing to trust so it re-shows the town chooser.
        if (quickZone && quickZone.active !== false) {
          saveSelectedTownId(quickZone.id);
        }
        return { zone: quickZone, inArea: true, fromQuick: true };
      }
      const msg = t(getGeoErrorKey(e?.message));
      setLocationError(msg);
      setLocating(false);
      // Center camera on Piler town center without creating a fake pickup
      if (mapInstance.current && !pickup?.lat) {
        fitMapToPoint(mapInstance.current, APP.mapCenter, getMapPadding(), 14.5);
      }
      if (e?.message === 'denied') {
        // Permission is off — always surface an actionable prompt on a tap,
        // even when a stale pin already exists.
        if (userInitiated && !startup) showLocationDeniedToast(t);
      } else if (e?.message === 'location_disabled') {
        if (userInitiated && !startup) {
          showLocationDisabledToast(t, () => {
            detectPickup({ userInitiated: true });
          });
        }
      } else if (userInitiated && !startup && !pickup?.lat && !isBillingError(e?.message)) {
        toast.error(msg);
      }
      return { error: e?.message || 'unavailable' };
    }
  }, [t, applyPickupFromGps, pickup, getMapPadding]);

  // Resolve a town before creating the map. GPS is convenient, but a denied
  // permission simply routes the passenger to the town selector instead of
  // leaving them on a misleading default Pileru map.
  useEffect(() => {
    if (!user?.uid || ridesLoaded) { setRidesRestoreTimedOut(false); return undefined; }
    const timer = setTimeout(() => setRidesRestoreTimedOut(true), 4000);
    return () => clearTimeout(timer);
  }, [user?.uid, ridesLoaded]);

  // The town list (platform config) has no timeout of its own. A rider with a
  // saved town shouldn't sit on the splash for it: after this long, Book opens
  // on the saved town and the live config corrects it when it arrives.
  useEffect(() => {
    if (platformConfigReady) return undefined;
    const timer = setTimeout(() => setConfigWaitTimedOut(true), STARTUP_CONFIG_WAIT_MS);
    return () => clearTimeout(timer);
  }, [platformConfigReady]);

  // "Try again" on the town-load screen: show "Trying…" until the config
  // answers, fails again, or a few seconds pass (a slow answer still lands).
  useEffect(() => {
    if (!townRetrying) return undefined;
    if (platformConfigReady || platformConfigError) {
      setTownRetrying(false);
      return undefined;
    }
    const timer = setTimeout(() => setTownRetrying(false), STARTUP_CONFIG_WAIT_MS);
    return () => clearTimeout(timer);
  }, [townRetrying, platformConfigReady, platformConfigError]);

  // Returning rider whose first, quiet location read failed: once Book is on
  // screen, ask Android once per launch, through its own pop-ups (allow
  // location / turn on device location). "No thanks" leaves Book usable with
  // the small note; the GPS button asks again. Replaces the old ask at app
  // launch (MainActivity), which showed over a blank screen.
  const askForLocationAfterStartup = useCallback(async (knownStatus = null) => {
    if (!isNativeApp() || startupLocationAskedRef.current) return;
    startupLocationAskedRef.current = true;
    const status = knownStatus || await checkNativeLocationStatus().catch(() => null);
    if (!status) return;
    if (status.permission === 'denied') {
      // Android won't show its pop-up again; point to Settings instead.
      showLocationDeniedToast(t);
      return;
    }
    if (status.permission !== 'granted' || !status.locationServicesEnabled) {
      await detectPickup({ userInitiated: true, startup: true });
      const after = await checkNativeLocationStatus().catch(() => null);
      if (after?.permission === 'granted' && !after.locationServicesEnabled) {
        setLocationError('');
        setStartupLocationOff(true);
      }
    }
  }, [t, detectPickup]);

  useEffect(() => {
    // Wait until the active-ride subscription has restored first. A page
    // refresh during a live booking must go straight back to that ride, never
    // briefly surface a location/town chooser underneath it. Capped at 4s
    // (ridesRestoreTimedOut) so a stuck/failed rides listener can't strand
    // the passenger on this screen forever.
    // A saved town can go ahead without the config after STARTUP_CONFIG_WAIT_MS
    // (it is checked against the towns known so far); a GPS-only start needs
    // the real town list, so it keeps waiting.
    const savedTownKnownNow = Boolean(readSelectedTownId()
      && getServiceZones({ activeOnly: true }).some((z) => z.id === readSelectedTownId()));
    if (
      (!platformConfigReady && !(configWaitTimedOut && savedTownKnownNow))
      || (user?.uid && !ridesLoaded && !ridesRestoreTimedOut)
      || activeRide
      || startupTownAttemptRef.current
    ) return;
    startupTownAttemptRef.current = true;

    // A town confirmed in ANY earlier session (saved to localStorage, so it
    // survives refreshes/relaunches) is trusted immediately — the passenger
    // already proved they're a Piler (or Kalikiri, etc.) rider. Never re-show
    // the blocking "Choose your town" / "Getting location..." card just
    // because the tab reloaded. GPS still refines the exact pickup pin
    // silently in the background, same as any other returning-user visit.
    const savedTownId = readSelectedTownId();
    const savedZone = savedTownId
      ? getServiceZones({ activeOnly: true }).find((z) => z.id === savedTownId)
      : null;
    if (savedZone) {
      // manual: false (not a real manual pick) so the background GPS refine
      // below can still silently correct this if the passenger has actually
      // travelled to a different live town since last time — the same
      // self-healing behaviour a fresh GPS-only detection would give.
      applyZoneById(savedZone.id, { manual: false });
      setTownReady(true);
      (async () => {
        // Location off or not allowed: ask right away. A quiet GPS read would
        // only time out first (~15 s with location off) before asking.
        if (isNativeApp()) {
          const status = await checkNativeLocationStatus().catch(() => null);
          if (status && (status.permission !== 'granted' || !status.locationServicesEnabled)) {
            askForLocationAfterStartup(status);
            return;
          }
        }
        const result = await detectPickup({ userInitiated: false });
        if (result?.error) askForLocationAfterStartup();
      })();
      return undefined;
    }

    const hasSavedTownFallback = Boolean(
      readSelectedTownId()
      && APP.zoneManuallySelected
      && APP.currentZoneActive !== false,
    );

    let cancelled = false;
    detectPickup({ userInitiated: true, startup: true }).then((result) => {
      if (cancelled) return;
      if (result?.zone && result.inArea && result.zone.active !== false) {
        setTownReady(true);
        return;
      }
      setLocating(false);
      // Rider said "No thanks" to turning location on (over the chooser): once
      // they pick a town, Book says location is off, as on the saved-town path.
      if (result?.error && isNativeApp()) {
        checkNativeLocationStatus().then((status) => {
          if (status?.permission === 'granted' && !status.locationServicesEnabled) setStartupLocationOff(true);
        }).catch(() => {});
      }
      // A remembered town is only a fallback when GPS cannot be obtained
      // (permission denied, services off, or a temporary device failure).
      // A real GPS fix outside every live town must show the selector instead
      // of incorrectly presenting the old town as bookable.
      setTownReady(Boolean(result?.error && hasSavedTownFallback));
    });
    return () => { cancelled = true; };
  }, [platformConfigReady, configWaitTimedOut, user?.uid, ridesLoaded, ridesRestoreTimedOut, activeRide, detectPickup, askForLocationAfterStartup]);

  useEffect(() => onZoneChange(() => setZoneRevision((revision) => revision + 1)), []);

  const chooseTown = useCallback((townId) => {
    const zone = applyZoneById(townId, { manual: true });
    if (!zone || zone.active === false) {
      clearSelectedTownId();
      toast.error(t('book.townUnavailable') || 'This town is not available yet.');
      return;
    }
    saveSelectedTownId(zone.id);
    setPickup((current) => (current && isWithinServiceArea(current.lat, current.lng) ? current : null));
    setDrop(null);
    setRouteInfo(null);
    setPickupValid(true);
    setLocationError('');
    setLocating(false);
    setStep('home');
    setTownReady(true);
    setShowTownSwitcher(false);
  }, [t]);

  const useCurrentLocationForTown = useCallback(async () => {
    const result = await detectPickup({ userInitiated: true });
    if (result?.zone && result.inArea && result.zone.active !== false) {
      setTownReady(true);
      setShowTownSwitcher(false);
      return;
    }
    if (!result?.error) {
      toast.error(t('book.locationOutsideTown') || 'Your location is outside our active service towns. Choose a town to continue.');
    }
  }, [detectPickup, t]);

  useEffect(() => () => {
    refineStopRef.current?.();
    refineStopRef.current = null;
  }, []);

  const swapLocations = () => {
    if (!pickup || !drop) return;
    const nextPickup = { ...drop, address: drop.address || drop.name, name: drop.name || drop.address };
    const nextDrop = { ...pickup, address: pickup.address || pickup.name, name: pickup.name || pickup.address };
    setPickup(nextPickup);
    setDrop(nextDrop);
  };

  const goHome = () => {
    setStep('home');
    setDrop(null);
    setRiderNote(null);
    setRouteInfo(null);
    setEditingPickup(false);
    if (routeLine.current) routeLine.current.setMap(null);
    clearMarkers(markers.current);
    if (pickup && mapInstance.current) {
      centerMapOnPickup(pickup);
    }
  };

  const openSearch = () => {
    setStep('search');
    setRouteInfo(null);
    if (!pickupValid || !pickup) {
      setEditingPickup(true);
      return;
    }
    setDrop(null);
    setEditingPickup(false);
    if (pickup && mapInstance.current) {
      centerMapOnPickup(pickup);
    }
  };

  // `areaSeed`: the colony/area place just picked from search, to centre on
  // (the drop state isn't set yet in that flow). `plain`: it is only a place to centre on,
  // not a big area to pin inside (an out-of-area search result), so no "big area" hint.
  const openPinPicker = (target, areaSeed = null, { plain = false } = {}) => {
    pinReturnStep.current = step === 'home' ? 'search' : step;
    setPinTarget(target);
    setPinAreaName(areaSeed && !plain ? (areaSeed.name || areaSeed.address || null) : null);
    setEditingPickup(false);
    userPannedMapRef.current = false;
    const existing = areaSeed || (target === 'pickup' ? pickup : drop);
    const center = existing?.lat
      ? { lat: existing.lat, lng: existing.lng }
      : (pickup?.lat ? { lat: pickup.lat, lng: pickup.lng } : (userGps?.lat ? { lat: userGps.lat, lng: userGps.lng } : APP.mapCenter));
    const ok = isWithinServiceArea(center.lat, center.lng);
    setPinValid(ok);
    if (existing?.name || existing?.address) {
      setPinPlace(existing);
      setPinLoading(false);
    } else {
      setPinPlace({
        ...center,
        name: t('book.pinApprox'),
        address: `${center.lat.toFixed(5)}, ${center.lng.toFixed(5)}`,
      });
      setPinLoading(true);
      reverseGeocode(center.lat, center.lng).then((p) => {
        setPinPlace(p);
        setPinLoading(false);
      }).catch(() => {
        setPinLoading(false);
      });
    }
    setStep('pin');
    if (mapInstance.current) {
      const safeTop = readSafePx('--safe-area-top');
      const safeBottom = readSafePx('--safe-area-bottom');
      const pinPadding = { top: 56 + safeTop, bottom: 220 + safeBottom, left: 24, right: 24 };
      applyMapSheetPadding(mapInstance.current, { bottom: 220 + safeBottom, top: 56 + safeTop });
      fitMapToPoint(mapInstance.current, center, pinPadding, 17.5);
    }
  };

  openPinPickerRef.current = openPinPicker;

  const closePinPicker = () => {
    setPickupCheck(null);
    setStep(pinReturnStep.current === 'pin' ? 'search' : pinReturnStep.current);
    setPinTarget(null);
    setPinPlace(null);
    setPinAreaName(null);
  };

  // "Book with Intertown": carry the pickup and this drop over to the Intertown tab.
  const goToIntertown = async () => {
    if (!dropRedirect?.ok || redirecting || !pinPlace) return;
    setRedirecting(true);
    hapticSelection();
    const state = await intertownHandover({
      platform, pickup, pin: pinPlace, reverseGeocode, fallbackName: t('book.pinApprox'),
    });
    setRedirecting(false);
    if (!state) {
      // The settings changed while the name was being looked up: fall back to the usual message.
      toast.error(t('book.outsideArea', { km: APP.serviceRadiusKm, city: APP.city }));
      return;
    }
    closePinPicker();
    navigate('/intertown', { state });
  };

  const confirmPin = async () => {
    if (!pinValid) return;
    // Sample live needle tip coordinate to guarantee 100% precision
    const livePoint = mapInstance.current
      ? getPointUnderVisualPin(mapInstance.current, '.rapido-center-pin', getMapPadding)
      : null;
    const target = livePoint || pinPlace;
    if (!target) return;

    let finalPlace = pinPlace;
    const isGeneric = !finalPlace?.name
      || finalPlace.name === t('book.pinApprox')
      || finalPlace.name === 'Selected location'
      || finalPlace.name === 'ఎంచుకున్న స్థానం';

    if (isGeneric || (livePoint && pinPlace && distanceBetweenM(livePoint, pinPlace) > 20)) {
      try {
        const geocodePromise = reverseGeocode(target.lat, target.lng);
        const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 1200));
        const fresh = await Promise.race([geocodePromise, timeoutPromise]);
        if (fresh && fresh.name && fresh.name !== t('book.pinApprox') && fresh.name !== 'Selected location') {
          finalPlace = fresh;
        }
      } catch {
        if (!finalPlace?.name) {
          finalPlace = {
            lat: target.lat,
            lng: target.lng,
            name: t('book.pinApprox') || 'Selected location',
            address: `${target.lat.toFixed(5)}, ${target.lng.toFixed(5)}`,
          };
        }
      }
    }

    const place = {
      ...finalPlace,
      lat: target.lat,
      lng: target.lng,
    };

    // Dragging the pin chose a place; it wasn't the rider panning the route
    // view, so the confirm screen must still frame the new route.
    userPannedMapRef.current = false;

    if (pinTarget === 'pickup') {
      refineStopRef.current?.();
      refineStopRef.current = null;
      manualPickupSelectedRef.current = true;
      const resolved = { ...place, source: 'map_pin', isApproximate: false };
      const checkOutcome = pickupCheck && drop
        ? pickupCheckOutcome(distanceBetweenM(pickupCheck, resolved))
        : null;
      if (checkOutcome === 'book') {
        // Same route within a few metres: the fare on screen still holds (the server
        // prices from the route distance), so don't fetch a new quote for it.
        lastFetchedRouteKeyRef.current = routeKeyFor(resolved, drop);
      } else if (checkOutcome === 'requote' && routeInfo?.isServerQuote) {
        setMovedFromFare({ fare: Number(routeInfo.fare), routeKey: routeKeyFor(resolved, drop) });
      }
      setPickup(resolved);
      savePickupCache(resolved);
      setPickupValid(true);
      setEditingPickup(false);
      setPickupCheck(null);
      setStep(drop ? 'confirm' : 'search');
      if (checkOutcome === 'book') handleBook(resolved);
    } else {
      setDrop({ ...place, source: 'map_pin' });
      if (pendingSaveShortcutRef.current) {
        const shortcutId = pendingSaveShortcutRef.current;
        pendingSaveShortcutRef.current = null;
        cachePlaceLocal(shortcutId === 'home' ? 'mtl_saved_home' : 'mtl_saved_work', place);
        toast.success(shortcutId === 'home' ? (t('book.savedHome') || 'Saved as Home') : (t('book.savedWork') || 'Saved as Work'));
      }
      setStep('confirm');
    }
    setPinTarget(null);
    setPinPlace(null);
    setPinAreaName(null);
  };

  const recenterForPinOrPickup = () => {
    hapticSelection();
    userPannedMapRef.current = false;

    // Confirm step: Both Pickup (A) & Drop (B) are set with route polyline -> Fit entire route!
    if (step === 'confirm' && pickup?.lat && drop?.lat && mapInstance.current) {
      fitMapToPoints(mapInstance.current, [pickup, drop], getMapPadding());
      return;
    }

    // Pin step: Pin Picker is open -> Center on pin position + refine with satellite GPS
    if (step === 'pin') {
      const cachedGps = lastGpsPlaceRef.current || (userGps?.lat ? { ...userGps, name: t('book.pinApprox') } : null);
      if (cachedGps) {
        centerMapOnPickup(cachedGps);
        setPinPlace(cachedGps);
        setPinValid(true);
      }
      getBestDevicePosition().then(async (pos) => {
        if (!pos?.lat) return;
        setUserGps({ lat: pos.lat, lng: pos.lng, accuracy: pos.accuracy });
        let freshPlace = {
          lat: pos.lat,
          lng: pos.lng,
          name: t('book.pinApprox') || 'Current Location',
          address: `${pos.lat.toFixed(5)}, ${pos.lng.toFixed(5)}`,
          accuracy: pos.accuracy,
        };
        try {
          const geocoded = await reverseGeocode(pos.lat, pos.lng);
          if (geocoded) freshPlace = { ...geocoded, accuracy: pos.accuracy };
        } catch {
          /* keep coords */
        }
        setPinPlace(freshPlace);
        setPinValid(isWithinServiceArea(pos.lat, pos.lng));
        centerMapOnPickup(freshPlace);
      }).catch((e) => {
        if (e?.message === 'denied') showLocationDeniedToast(t);
        else if (e?.message === 'location_disabled') showLocationDisabledToast(t, recenterForPinOrPickup);
      });
      return;
    }

    // Home / Search step: User explicitly tapped Recenter FAB -> Jump to fresh live GPS!
    manualPickupSelectedRef.current = false;
    setEditingPickup(false);

    // 1. Instant optimistic visual center if GPS coordinates exist in memory
    const cachedGps = lastGpsPlaceRef.current || (userGps?.lat ? { ...userGps, source: 'gps', name: t('book.pinApprox') } : null);
    if (cachedGps) {
      setPickup(cachedGps);
      setPickupValid(true);
      savePickupCache(cachedGps);
      centerMapOnPickup(cachedGps);
    }

    // 2. Force fresh high-accuracy GPS fix & reverse-geocode
    detectPickup({ userInitiated: true }).catch(() => {});
  };

  useEffect(() => {
    if (activeRide) navigate(`/active/${activeRide.id}`, { replace: true });
  }, [activeRide, navigate]);

  // Play review is deliberately evaluated only after the passenger is back on
  // the visible, idle Book tab. It is never part of booking, routing, payment,
  // or the rating-sheet submission path.
  useEffect(() => {
    if (!isTabActive || step !== 'home' || activeRide || booking || !user?.uid || !hasQueuedPlayReview(user.uid)) return undefined;
    let cancelled = false;
    let retryTimer = null;
    // The completed-ride count is updated by a backend trigger a moment after
    // the ride doc flips to completed. Retry only a couple of times to ride out
    // that lag, then stop — the queued record survives and the next home-screen
    // visit re-evaluates it. Never an open-ended poll.
    const MAX_TRIES = 3;
    let tries = 0;

    const evaluate = async () => {
      if (cancelled || tries >= MAX_TRIES) return;
      tries += 1;
      const stats = await getUserRideStats();
      if (cancelled || !stats) return;
      const outcome = await requestQueuedPlayReview({
        uid: user.uid,
        completedRides: stats.completedCount,
      });
      if (!cancelled && outcome.state === 'waiting' && tries < MAX_TRIES) {
        retryTimer = window.setTimeout(evaluate, 4000);
      }
    };

    const initialTimer = window.setTimeout(evaluate, 1800);
    return () => {
      cancelled = true;
      window.clearTimeout(initialTimer);
      if (retryTimer) window.clearTimeout(retryTimer);
    };
  }, [isTabActive, step, activeRide, booking, user?.uid]);

  useEffect(() => {
    const dropPlace = location.state?.rebookDrop || location.state?.externalDrop;
    const pickupPlace = location.state?.rebookPickup;
    if (!dropPlace?.lat || activeRide) return;
    setDrop(dropPlace);
    if (pickupPlace?.lat) {
      setPickup(pickupPlace);
      setPickupValid(isWithinServiceArea(pickupPlace.lat, pickupPlace.lng));
    } else {
      const cached = loadPickupCache();
      if (cached?.lat) {
        setPickup(cached);
        setPickupValid(isWithinServiceArea(cached.lat, cached.lng));
      }
    }
    setStep('confirm');
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate, activeRide]);

  // Live event listener when app is opened via WhatsApp / Google Maps while already mounted
  useEffect(() => {
    const handleExternalDrop = (e) => {
      const externalDrop = e.detail;
      if (!externalDrop?.lat || !externalDrop?.lng || activeRide) return;
      handleDropSelected(externalDrop);
    };
    window.addEventListener('mt_external_drop', handleExternalDrop);
    return () => window.removeEventListener('mt_external_drop', handleExternalDrop);
  }, [activeRide, handleDropSelected]);

  useEffect(() => {
    if (step === 'confirm' && (!pickup || !pickup.lat)) {
      const cached = loadPickupCache();
      if (cached?.lat) {
        setPickup(cached);
        setPickupValid(isWithinServiceArea(cached.lat, cached.lng));
      } else {
        detectPickup({ userInitiated: false });
      }
    }
  }, [step, pickup, detectPickup]);

  // Auto-detect & recenter when app returns to foreground (e.g. user toggled Location in Settings)
  useEffect(() => {
    if (!isNativeApp()) return undefined;
    let sub = null;
    CapApp.addListener('appStateChange', async ({ isActive }) => {
      if (!isActive) return;
      if (stepRef.current !== 'home' || manualPickupSelectedRef.current) return;
      try {
        const status = await checkNativeLocationStatus();
        if (status.permission === 'granted' && status.locationServicesEnabled) {
          detectPickup({ userInitiated: false });
        }
      } catch {
        /* ignore */
      }
    }).then((s) => { sub = s; });

    return () => {
      sub?.remove?.();
    };
  }, [detectPickup]);

  useEffect(() => {
    const el = sheetRef.current;
    const screen = el?.closest('.rapido-screen');
    if (!el || !screen) return undefined;

    let rafId = null;
    let debounceTimer = null;
    let lastAppliedH = 0;

    const apply = () => {
      const h = Math.round(el.getBoundingClientRect().height);
      if (Math.abs(h - lastAppliedH) < 2) return;
      lastAppliedH = h;

      screen.style.setProperty('--sheet-h', `${h}px`);
      const map = mapInstance.current;
      if (!map) return;

      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        const navH = step === 'home' ? 58 : 0;
        const safeBottom = Number.parseInt(
          getComputedStyle(document.documentElement).getPropertyValue('--safe-area-bottom'),
          10,
        ) || 0;
        const safeTop = Number.parseInt(
          getComputedStyle(document.documentElement).getPropertyValue('--safe-area-top'),
          10,
        ) || 0;
        const bottomPad = h + navH + safeBottom;
        applyMapSheetPadding(map, { bottom: bottomPad, top: 56 + safeTop });
        const target = mapCenterTargetRef.current;
        if (target && !userPannedMapRef.current && !programmaticMapMoveRef.current && step === 'home') {
          fitMapToPoint(map, target, getMapPadding());
        } else if (step === 'confirm' && !userPannedMapRef.current && routeLine.current && pickup?.lat && drop?.lat) {
          // The route is fitted as soon as it arrives, while the sheet still
          // says "Calculating fare"; the fare rows then make it much taller.
          // Refit to the final sheet so the drop isn't left under it.
          fitMapToPoints(map, [pickup, drop], getMapPadding());
        }
      }, 50);
    };

    apply();
    const ro = new ResizeObserver(() => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(apply);
    });
    ro.observe(el);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      clearTimeout(debounceTimer);
      ro.disconnect();
    };
  }, [step, locating, pickup, drop, getMapPadding]);

  useEffect(() => {
    if (!townReady || mapInitDone.current) return undefined;
    let cancelled = false;
    (async () => {
      try {
        if (!mapRef.current) return;
        const map = await createMap(mapRef.current, APP.mapCenter, { allowNative: true });
        if (cancelled) { destroyMap(map); return; }
        mapInstance.current = map;
        mapInitDone.current = true;
        setMapReady(true);
        map.addListener('dragstart', () => {
          if (programmaticMapMoveRef.current) return;
          userPannedMapRef.current = true;
          mapCenterTargetRef.current = null;
        });
        const initialPickup = pickupRef.current;
        if (initialPickup && isWithinServiceArea(initialPickup.lat, initialPickup.lng)) {
          setPickupValid(true);
          centerMapOnPickup(initialPickup);
        }
        const cached = loadPickupCache();
        if (cached && isWithinServiceArea(cached.lat, cached.lng)) {
          setPickup(cached);
          setPickupValid(isWithinServiceArea(cached.lat, cached.lng));
          setLocating(false);
          centerMapOnPickup(cached);
        }
      } catch (e) {
        if (!cancelled) {
          setMapError(isBillingError(e?.message) ? t('book.billingError') : t('book.mapsError'));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [townReady]);

  // `APP` itself is mutable, so zoneEvents provides the reactive edge. This
  // effect owns camera movement because Book owns the Google Map instance.
  useEffect(() => {
    if (!townReady || !mapReady || !mapInstance.current) return;
    // A valid pickup already resolved (the map-init effect's cached/GPS pin,
    // or one set since) means the camera has already been correctly placed
    // there — resetting to the zone's generic center here would visibly yank
    // it away right after landing. Only reset to the generic center when
    // there's genuinely no known pickup yet (fresh zone, none picked so far).
    if (pickupRef.current && isWithinServiceArea(pickupRef.current.lat, pickupRef.current.lng)) return;
    const map = mapInstance.current;
    programmaticMapMoveRef.current = true;
    mapCenterTargetRef.current = { ...APP.mapCenter };
    map.panTo(APP.mapCenter);
    if (typeof map.setZoom === 'function') map.setZoom(14.5);
    onceMapIdle(map, () => { programmaticMapMoveRef.current = false; });
  }, [townReady, mapReady, zoneRevision]);

  // The primary tabs keep Book mounted so Google Maps does not have to rebuild
  // its vector basemap on every return. Once the hidden tab is visible again,
  // notify Maps about its container and preserve the passenger's camera exactly.
  useEffect(() => {
    if (!isTabActive || !mapReady || !mapInstance.current) return undefined;
    // The native map keeps its own camera; it is only hidden/shown (below).
    if (mapInstance.current.__native) return undefined;

    let secondFrame = null;
    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => {
        const map = mapInstance.current;
        if (!map) return;
        const center = map.getCenter?.();
        const zoom = map.getZoom?.();
        window.google?.maps?.event?.trigger(map, 'resize');
        if (center) map.setCenter(center);
        if (Number.isFinite(zoom)) map.setZoom(zoom);
      });
    });

    return () => {
      cancelAnimationFrame(firstFrame);
      if (secondFrame) cancelAnimationFrame(secondFrame);
    };
  }, [isTabActive, mapReady]);

  // Native map: Book stays mounted behind the other tabs, but its map sits
  // under the whole WebView, so hide it (and its tap check) while the tab is
  // hidden. Released for good when Book unmounts (e.g. a ride starts).
  useEffect(() => {
    const map = mapInstance.current;
    if (!mapReady || !map?.__native) return;
    map.setVisible(Boolean(isTabActive)).catch?.(() => {});
  }, [isTabActive, mapReady]);

  useEffect(() => () => {
    destroyMap(mapInstance.current);
    mapInstance.current = null;
  }, []);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible' || step !== 'home' || !pickup?.lat) return;
      centerMapOnPickup(pickup);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [step, pickup, centerMapOnPickup]);

  // When Intertown could take an out-of-area trip from this pickup, the drop search also finds towns
  // beyond the local circle (by preference, not restriction), so "Tirupati" shows up and choosing it
  // leads to "Book with Intertown". Otherwise it stays limited to the circle, as before.
  const dropSearchBeyondArea = useMemo(() => intertownCanTakeOutsideDrop({ platform, pickup }), [platform, pickup]);
  useEffect(() => {
    if (step !== 'search' || !dropAcRef.current) return undefined;
    mountPlaceAutocomplete(dropAcRef.current, t('book.whereGoing'), handleDropSelected, { searchBeyondArea: dropSearchBeyondArea })
      .catch(console.error);
    return undefined;
  }, [step, t, handleDropSelected, dropSearchBeyondArea]);

  useEffect(() => {
    if (!editingPickup || !pickupAcRef.current) return undefined;
    mountPlaceAutocomplete(pickupAcRef.current, t('book.pickupPlaceholder', { city: APP.city }), handlePlaceSelect(setPickup))
      .catch(console.error);
    return undefined;
  }, [editingPickup, handlePlaceSelect, t]);

  // A plain string, so effects keyed on it only re-run when the points change.
  const routePointsKey = routeKeyFor(pickup, drop);

  const lastFetchedRouteKeyRef = useRef('');
  const cachedRouteInfoRef = useRef(null);
  const routeCameraFittedKeyRef = useRef('');

  useEffect(() => {
    if (!routePointsKey || !pickup || !drop || !mapInstance.current || step === 'pin') {
      if (!pickup || !drop) {
        setRouteInfo(null);
        cachedRouteInfoRef.current = null;
        lastFetchedRouteKeyRef.current = '';
        routeCameraFittedKeyRef.current = '';
      }
      return undefined;
    }
    let cancelled = false;

    (async () => {
      try {
        let info = cachedRouteInfoRef.current;
        const needsFetch = lastFetchedRouteKeyRef.current !== routePointsKey || !info;

        if (needsFetch) {
          setRouteLoading(true);
          const directions = await getDirections(pickup, drop);
          // The rider may have swapped or edited the route while Directions was
          // loading; a newer lookup already started. The server allows one quote
          // per second, so don't spend it on a route nobody is looking at.
          if (cancelled) return;
          // Two lookups close together (swap right after the first fare) can hit
          // that one-per-second limit; wait it out instead of giving up.
          const quote = await retryWhenRateLimited(
            () => getRideQuote({
              pickup,
              destination: drop,
              distanceKm: directions.distanceKm,
            }),
            { shouldStop: () => cancelled },
          );
          if (!Number.isFinite(quote.fare) || !Number.isFinite(quote.distanceKm)) {
            throw new Error('Invalid fare quote');
          }
          // Navigation facts must always describe the exact Google polyline we
          // draw. The server quote remains the fare authority and its distance
          // is retained separately for the booking payload/fare breakdown.
          // This prevents a long detour line ever being labelled with a shorter
          // straight-line fallback estimate.
          info = {
            ...directions,
            distanceKm: directions.distanceKm,
            durationMin: directions.durationMin,
            fareDistanceKm: quote.distanceKm,
            fare: quote.fare,
            fareSnapshot: quote.fareSnapshot,
            // Nearest auto / ETA / tip chips; absent on an older server.
            pickupInfo: quote.pickupInfo || null,
            // Waiting-charge terms (only present while the admin mode is `on`).
            waiting: quote.waiting || null,
            pickupInfoAt: Date.now(),
            isServerQuote: true,
            isEstimating: false,
          };
          if (cancelled) return;
          cachedRouteInfoRef.current = info;
          lastFetchedRouteKeyRef.current = routePointsKey;
          setTipChoice(0); // a new quote means new chips — never carry an old tip over
          setRouteInfo(info);
        }

        // If route and markers already exist on map, no need to redraw
        if (routeLine.current && markers.current.length >= 2 && !needsFetch) {
          return;
        }

        clearMarkers(markers.current);
        const [mA, mB, line] = await Promise.all([
          placeMarker(mapInstance.current, pickup, 'A'),
          placeMarker(mapInstance.current, drop, 'B'),
          drawRoute(mapInstance.current, info.polyline),
        ]);
        if (cancelled) {
          mA?.setMap(null);
          mB?.setMap(null);
          line?.setMap(null);
          return;
        }
        markers.current.push(mA, mB);
        if (routeLine.current) routeLine.current.setMap(null);
        routeLine.current = line;
        // Fit the complete route once per coordinate pair. Repeated fitBounds
        // calls while the confirm sheet/route data settles make the map visibly
        // jump. Padding is applied first so the single fit uses the final layout.
        if (!userPannedMapRef.current && routeCameraFittedKeyRef.current !== routePointsKey) {
          applyMapSheetPadding(mapInstance.current, {
            bottom: getMapPadding().bottom,
            top: getMapPadding().top,
          });
          routeCameraFittedKeyRef.current = routePointsKey;
          fitMapToPoints(mapInstance.current, [pickup, drop], getMapPadding());
        }
      } catch (e) {
        if (!cancelled) {
          setRouteInfo(null);
          const reason = e?.details?.reason || e?.customData?.details?.reason || '';
          if (reason === 'booking_paused' || reason === 'emergency_stop') {
            toast.error(String(e?.message || '') || t('book.bookingPaused'));
          } else if (isBillingError(e?.message)) {
            setMapError(t('book.billingError'));
          } else {
            // The quote is requested automatically (and rate-limited to 1/s),
            // so a "too many requests" here is noise — keep the route message.
            // (It only reaches here after retryWhenRateLimited gave up.)
            const knownKey = bookingErrorKey(e);
            toast.error(knownKey && knownKey !== 'book.errTooMany' ? t(knownKey) : t('book.routeError'));
          }
        }
      } finally {
        if (!cancelled) setRouteLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [routePointsKey, step]);

  useEffect(() => {
    if ((step !== 'pin' && step !== 'home') || !mapReady || !mapInstance.current) return undefined;
    const map = mapInstance.current;
    if (routeLine.current) {
      routeLine.current.setMap(null);
      routeLine.current = null;
    }
    clearMarkers(markers.current);

    let timer;
    const lastKey = { current: '' };

    const runGeocode = (lat, lng, { immediate = false } = {}) => {
      const key = `${lat.toFixed(5)},${lng.toFixed(5)}`;
      if (key === lastKey.current && !immediate) return;
      clearTimeout(timer);

      const ok = isWithinServiceArea(lat, lng);

      if (step === 'pin') {
        // If user hasn't dragged yet and pinPlace already has a valid address/name matching initial point, preserve it
        if (!userPannedMapRef.current && pinPlace?.name && pinPlace?.name !== t('book.pinApprox')) {
          const dist = distanceBetweenM({ lat, lng }, { lat: pinPlace.lat, lng: pinPlace.lng });
          if (dist < 15) return;
        }

        setPinValid(ok);
        setPinPlace({
          lat,
          lng,
          name: ok ? t('book.pinApprox') : t('book.pickupOutside'),
          address: ok
            ? `${lat.toFixed(5)}, ${lng.toFixed(5)}`
            : t('book.outsideArea', { km: APP.serviceRadiusKm, city: APP.city }),
        });

        if (!ok) {
          setPinLoading(false);
          lastKey.current = key;
          return;
        }

        const fetch = async () => {
          const seq = ++pinGeocodeSeq.current;
          setPinLoading(true);
          lastKey.current = key;
          try {
            const place = await reverseGeocode(lat, lng);
            if (seq !== pinGeocodeSeq.current) return;
            setPinPlace(place);
            setPinValid(true);
          } catch {
            if (seq !== pinGeocodeSeq.current) return;
            setPinPlace({
              lat,
              lng,
              name: t('book.pinApprox'),
              address: `${lat.toFixed(5)}, ${lng.toFixed(5)}`,
            });
            setPinValid(true);
          } finally {
            if (seq === pinGeocodeSeq.current) setPinLoading(false);
          }
        };

        if (immediate) fetch();
        else timer = setTimeout(fetch, 90);
      } else if (step === 'home') {
        // HOME STEP: Live Map Drag & Pan updates pickup directly
        refineStopRef.current?.();
        refineStopRef.current = null;
        manualPickupSelectedRef.current = true;
        setPickupValid(ok);

        // Movement threshold: If user dragged < 10m from existing named pickup, preserve address
        if (pickup?.lat && pickup?.lng && pickup?.name && pickup?.name !== t('book.pinApprox')) {
          const dist = distanceBetweenM({ lat, lng }, { lat: pickup.lat, lng: pickup.lng });
          if (dist < 10 && !immediate) {
            setPickup((prev) => ({
              ...prev,
              lat,
              lng,
              source: 'map_pin',
            }));
            setLocating(false);
            return;
          }
        }

        setLocating(true);
        const fetch = async () => {
          const seq = ++pinGeocodeSeq.current;
          lastKey.current = key;
          try {
            const place = await reverseGeocode(lat, lng);
            if (seq !== pinGeocodeSeq.current) return;
            const resolved = { ...place, source: 'map_pin' };
            setPickup(resolved);
            savePickupCache(resolved);
            setPickupValid(ok);
          } catch {
            if (seq !== pinGeocodeSeq.current) return;
            const approxPlace = {
              lat,
              lng,
              source: 'map_pin',
              name: ok ? t('book.pinApprox') : t('book.pickupOutside'),
              address: `${lat.toFixed(5)}, ${lng.toFixed(5)}`,
            };
            setPickup(approxPlace);
            savePickupCache(approxPlace);
          } finally {
            if (seq === pinGeocodeSeq.current) setLocating(false);
          }
        };

        if (immediate) fetch();
        else timer = setTimeout(fetch, 250);
      }
    };

    const onCenterChange = (point, opts = {}) => {
      if (!point) return;
      runGeocode(point.lat, point.lng, opts);
    };

    const unbindPin = bindPinnedCenterPin(map, {
      getPadding: getMapPadding,
      onDragStart: () => {
        document.body.classList.add('rapido-pin-dragging');
        userPannedMapRef.current = true;
        if (step === 'home') {
          setLocating(true);
          refineStopRef.current?.();
          refineStopRef.current = null;
          manualPickupSelectedRef.current = true;
        }
      },
      onDragEnd: () => {
        document.body.classList.remove('rapido-pin-dragging');
      },
      onCommitCenter: onCenterChange,
    });

    return () => {
      clearTimeout(timer);
      document.body.classList.remove('rapido-pin-dragging');
      unbindPin();
    };
    // mapReady: bind as soon as the map exists. Without it, a map that loads
    // after this effect's first run (always so for the native map, which takes
    // a moment longer to create) never got the drag → pickup binding.
  }, [step, pinTarget, t, getMapPadding, mapReady]);

  useEffect(() => {
    if (step === 'home') clearMarkers(markers.current);
  }, [step, pickup, drop]);

  // On confirm: if far pickup or booker outside Piler, ask once per pickup pin.
  useEffect(() => {
    if (step !== 'confirm' || !pickup) return;
    const check = evalBookForOther(userGps, pickup);
    const key = check.prompt
      ? `${check.reason}:${pickup.lat.toFixed(4)},${pickup.lng.toFixed(4)}`
      : '';
    if (!check.prompt) {
      if (forOtherKeyRef.current) {
        forOtherKeyRef.current = '';
        setForOtherDecision(null);
        setRiderDetails(null);
      }
      return;
    }
    // Already answered on home (out-of-town early) or earlier for this pickup.
    if (forOtherDecision) {
      forOtherKeyRef.current = key;
      return;
    }
    if (forOtherKeyRef.current === key) return;
    forOtherKeyRef.current = key;
    setForOtherDecision(null);
    setRiderDetails(null);
    setForOtherSheet({ reason: check.reason, distanceKm: check.distanceKm });
  }, [userGps, pickup, step, forOtherDecision]);

  // Out of town + logged in → ask on home, then guide to set Piler pickup.
  useEffect(() => {
    if (!user?.uid || activeRide) return;
    if (step !== 'home') return;
    if (!userGps?.lat || !userGps?.lng) return;
    if (isWithinServiceArea(userGps.lat, userGps.lng)) return;
    if (forOtherDecision || forOtherSheet) return;
    if (wasOutOfTownAsked()) return;
    setForOtherSheet({ reason: 'outOfTown', early: true });
  }, [user?.uid, activeRide, step, userGps, forOtherDecision, forOtherSheet]);

  const guideSetPickupInTown = useCallback(() => {
    if (pickupValid && pickup) return;
    setEditingPickup(true);
    setStep('search');
    toast(t('book.setPickupInTown', { city: APP.city }), { icon: '📍', duration: 5000 });
  }, [pickupValid, pickup, t]);

  // Availability (nearest auto, ETA, tip chips) goes stale: the route cache only
  // refreshes when coordinates change, so a rider who waits on the confirm
  // screen — or returns to this still-mounted tab — would keep an old ETA,
  // an old "nobody online" message and old tip options. Give it its own expiry.
  const routeInfoRef = useRef(null);
  routeInfoRef.current = routeInfo;
  const tipChoiceRef = useRef(0);
  tipChoiceRef.current = tipChoice;
  const refreshingPickupRef = useRef(false);

  const refreshPickupInfo = useCallback(async () => {
    const info = routeInfoRef.current;
    if (!info?.isServerQuote || !pickup?.lat || !drop?.lat || refreshingPickupRef.current) return;
    refreshingPickupRef.current = true;
    const keyAtStart = lastFetchedRouteKeyRef.current;
    try {
      const q = await getRideQuote({ pickup, destination: drop, distanceKm: info.distanceKm });
      // The rider may have changed pickup/drop while this was in flight.
      if (!Number.isFinite(q.fare) || lastFetchedRouteKeyRef.current !== keyAtStart) return;
      const cur = routeInfoRef.current;
      if (!cur) return;
      const next = {
        ...cur, fare: q.fare, fareSnapshot: q.fareSnapshot, pickupInfo: q.pickupInfo || null, waiting: q.waiting || null, pickupInfoAt: Date.now(),
      };
      cachedRouteInfoRef.current = next;
      setRouteInfo(next);
      // A tip the rider had picked may no longer be offered. Drop it visibly —
      // never leave a price on screen that the server would refuse.
      if (tipChoiceRef.current > 0 && selectedTip(next.pickupInfo, tipChoiceRef.current) === 0) {
        setTipChoice(0);
        toast(t('book.errTipChanged'), { icon: 'ℹ️', duration: 5000, id: 'tip-changed' });
      }
    } catch { /* keep what we have; the server re-checks at booking anyway */ }
    finally { refreshingPickupRef.current = false; }
  }, [pickup, drop, t]);

  useEffect(() => {
    if (step !== 'confirm' || !isTabActive || booking) return undefined;
    const check = () => {
      if (pickupInfoIsStale(routeInfoRef.current, Date.now(), PICKUP_INFO_MAX_AGE_MS)) refreshPickupInfo();
    };
    check(); // returning to the tab / confirm screen after a while refreshes at once
    const id = setInterval(check, PICKUP_INFO_CHECK_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [step, isTabActive, booking, refreshPickupInfo]);

  // `checkedPickup`: the pickup the rider just confirmed on the check screen
  // (state hasn't caught up yet in that same tap).
  const handleBook = async (checkedPickup = null) => {
    const bookPickup = checkedPickup || pickup;
    if (!online) {
      toast.error(t('network.offlineBody') || 'Reconnect to book your ride.');
      return;
    }
    if (!bookPickup || !drop || !routeInfo || booking || activeRide) return;
    if (bookingLockRef.current) return;
    if (!isWithinServiceArea(bookPickup.lat, bookPickup.lng)) {
      toast.error(t('book.outsideArea', { km: APP.serviceRadiusKm, city: APP.city }));
      setPickupValid(false);
      return;
    }
    if (APP.bookingEnabled === false) {
      toast.error((APP.modeMessage && APP.modeMessage.trim()) || t('book.bookingPaused'));
      return;
    }

    const check = evalBookForOther(userGps, bookPickup);
    if (check.prompt && !forOtherDecision) {
      setForOtherSheet({ reason: check.reason, distanceKm: check.distanceKm });
      return;
    }
    if (forOtherDecision === 'other' && !riderDetails?.riderPhone) {
      setForOtherSheet({ reason: check.reason || 'farPickup', distanceKm: check.distanceKm });
      return;
    }
    // GPS pickup the rider never looked at: show it on the pin map once first.
    if (!checkedPickup && pickupNeedsCheck(bookPickup)) {
      hapticSelection();
      setPickupCheck({ lat: bookPickup.lat, lng: bookPickup.lng });
      openPinPicker('pickup');
      return;
    }

    bookingLockRef.current = true;
    setBooking(true);
    hapticSelection(); // instant tactile confirm — the radar is already showing
    warmChime(); // unlock the audio context now so the "Anna assigned" chime can play later
    const ridePayload = {
      customerName: userProfile?.name || 'Customer',
      pickup: { address: bookPickup.address, lat: bookPickup.lat, lng: bookPickup.lng },
      destination: {
        name: drop.name || drop.address || 'Drop location',
        address: drop.address || drop.name || '',
        lat: drop.lat,
        lng: drop.lng,
      },
      paymentMode: 'cash',
      distanceKm: routeInfo.fareDistanceKm ?? routeInfo.distanceKm,
    };
    // The server re-checks this against its own nearest-auto distance.
    const bookingTip = selectedTip(routeInfo.pickupInfo, tipChoice);
    if (bookingTip > 0) ridePayload.tip = bookingTip;
    // Echo back the id of the terms on screen so the server can confirm the rider
    // saw the CURRENT terms. Nothing is sent when no terms were shown.
    const shownTerms = waitingTermsFromQuote(routeInfo.waiting);
    if (shownTerms) ridePayload.waitingTermsId = shownTerms.termsId;
    if (riderNote) ridePayload.riderNote = riderNote;
    if (forOtherDecision === 'other' && riderDetails) {
      ridePayload.isBookingForOther = true;
      ridePayload.riderName = riderDetails.riderName;
      ridePayload.riderPhone = riderDetails.riderPhone;
    }
    try {
      const created = await createRide(user.uid, ridePayload);
      hapticSuccess();
      setRiderNote(null);
      if (created?.id) {
        const initialRide = {
          id: created.id,
          status: 'searching',
          pickup: ridePayload.pickup,
          destination: ridePayload.destination,
          fare: created.fare,
          distanceKm: created.distanceKm,
          otp: created.otp,
          customerId: user.uid,
          customerName: ridePayload.customerName,
        };
        navigate(`/active/${created.id}`, { state: { initialRide } });
      } else {
        toast.error(t('book.bookError'));
      }
    } catch (e) {
      console.error(e);
      const msg = String(e?.message || '');
      const detailsRideId = e?.details?.rideId || e?.customData?.details?.rideId;
      // See classifyBookingError: a bare failed-precondition is NOT "active ride".
      const { kind, key: knownKey } = classifyBookingError(e);

      if (kind === 'paused') {
        toast.error(msg || t('book.bookingPaused'));
      } else if (kind === 'active') {
        toast.error(t('book.activeRideExists') || t('book.bookError'));
        let openId = detailsRideId || activeRide?.id || null;
        if (!openId && user?.uid) {
          openId = await findActiveRideId(user.uid);
        }
        if (openId) {
          navigate(`/active/${openId}`, { replace: true });
          return;
        }
      } else if (kind === 'outside') {
        toast.error(t('book.outsideArea', { km: APP.serviceRadiusKm, city: APP.city }));
      } else if (kind === 'known' && knownKey === 'book.errBlocked') {
        const supportUrl = buildTelUrl();
        toast.error(
          <span style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {t('book.errBlocked')}
            {supportUrl !== '#' && (
              <a href={supportUrl} style={{ fontWeight: 700 }}>{t('book.callSupport')}</a>
            )}
          </span>,
          { duration: 8000 },
        );
      } else if (kind === 'known' && knownKey === 'book.errTipChanged') {
        // The nearest auto moved since the quote, so the allowed tips changed.
        // Drop the rider's pick, refresh the chips, and ask them to review —
        // they are never charged an amount they did not choose.
        setTipChoice(0);
        tipChoiceRef.current = 0; // so the refresh below doesn't also toast
        await refreshPickupInfo();
        toast.error(t('book.errTipChanged'), { duration: 6000 });
      } else if (kind === 'known' && knownKey === 'book.errWaitingTermsChanged') {
        // An admin edited the waiting terms after the rider saw them. Re-fetch so
        // the screen shows the new terms, then ask them to review before booking —
        // they are never booked under terms they did not see.
        await refreshPickupInfo();
        toast.error(t('book.errWaitingTermsChanged'), { duration: 6000 });
      } else if (kind === 'known') {
        toast.error(t(knownKey));
      } else {
        toast.error(t('book.bookError'));
      }
    } finally { setBooking(false); bookingLockRef.current = false; }
  };

  const recentPlaces = (() => {
    const seen = new Set();
    const list = [];
    for (const ride of rides || []) {
      const dest = ride.destination;
      if (!dest?.lat || !dest?.name) continue;
      const key = `${dest.lat.toFixed(4)},${dest.lng.toFixed(4)}`;
      if (seen.has(key)) continue;
      seen.add(key);
      list.push({
        address: dest.name,
        name: dest.name,
        lat: dest.lat,
        lng: dest.lng,
      });
      if (list.length >= 4) break;
    }
    return list;
  })();

  // Only render saved destinations that have a real, usable location. An
  // unset Home/Work entry should remain available through Profile, but should
  // not appear as a misleading destination shortcut here.
  const savedDestinationShortcuts = [
    savedHome?.lat && savedHome?.lng ? {
      id: 'home',
      label: t('places.home') || 'Home',
      place: savedHome,
      Icon: HomeChipIcon,
    } : null,
    savedWork?.lat && savedWork?.lng ? {
      id: 'work',
      label: t('places.work') || 'Work',
      place: savedWork,
      Icon: WorkChipIcon,
    } : null,
  ].filter(Boolean);

  const selectRecent = (place) => {
    if (!pickupValid || !pickup) {
      toast.error(t('book.setPickupFirst'));
      return;
    }
    handleDropSelected(place);
  };

  // GPS pin dropped, street address not back yet: say so instead of showing
  // raw coordinates. Display only; the pickup object itself is unchanged.
  const pickupAwaitingAddress = pickupAddressPending
    && pickup?.source === 'gps' && pickup?.name === t('book.pinApprox');
  const pickupLabel = pickupAwaitingAddress
    ? t('book.gettingAddress')
    : (pickup?.address || pickup?.name)
    ? truncate(pickup.address || pickup.name, 48)
    : (locationError || (!pickupValid ? t('book.pickupOutside') : (locating ? t('book.locating') : t('book.setPickup'))));
  const pickupTitle = pickupAwaitingAddress
    ? t('book.gettingAddress')
    : (pickup?.name || pickup?.address)
    ? truncate(pickup.name || pickup.address, 36)
    : (locationError || (!pickupValid ? t('book.pickupOutside') : (locating ? t('book.locating') : t('book.setPickup'))));
  const pickupSub = pickupAwaitingAddress || !pickupValid || !pickup?.address || pickup?.name === pickup?.address
    ? ''
    : truncate(pickup.address, 52);
  const sheetClass = `rapido-sheet open${step === 'home' ? ' rapido-sheet-home' : ''}${step === 'search' ? ' rapido-sheet-search' : ''}${step === 'confirm' ? ` rapido-sheet-confirm${confirmMinimized ? ' collapsed' : ''}` : ''}${step === 'pin' ? ' rapido-sheet-pin' : ''}`;
  const pinName = pinPlace?.name || t('book.pinApprox');
  const pinAddr = pinPlace?.address && pinPlace.address !== pinPlace.name
    ? truncate(pinPlace.address, 64)
    : (pinLoading ? t('book.pinLoading') : '');
  const fareDisplay = useMemo(() => {
    const snapshot = routeInfo?.fareSnapshot || {};
    const base = Number.isFinite(Number(snapshot.baseFare)) ? Number(snapshot.baseFare) : APP.fare.base;
    const perKm = Number.isFinite(Number(snapshot.perKm)) ? Number(snapshot.perKm) : APP.fare.perKm;
    const distanceCharge = Number.isFinite(Number(snapshot.distanceCharge))
      ? Number(snapshot.distanceCharge)
      : Math.max(0, Number(routeInfo?.fare || 0) - base);
    const distanceKm = Number.isFinite(Number(snapshot.distanceKm))
      ? Number(snapshot.distanceKm)
      : (routeInfo?.fareDistanceKm ?? routeInfo?.distanceKm);
    const appliedMinimum = snapshot.appliedMinimum === true;
    const minimum = Number.isFinite(Number(snapshot.minimum)) ? Number(snapshot.minimum) : APP.fare.minimum;
    const cancellationFee = Number(snapshot.cancellationFee || 0);
    const isNightFare = snapshot.isNightFare === true;
    const nightSurcharge = Number(snapshot.nightSurcharge || 0);
    const isRainFare = snapshot.isRainFare === true;
    const rainSurcharge = Number(snapshot.rainSurcharge || 0);
    return {
      base, perKm, distanceCharge, distanceKm, appliedMinimum, minimum, cancellationFee,
      isNightFare, nightSurcharge, isRainFare, rainSurcharge,
    };
  }, [routeInfo]);
  const pickupInfo = routeInfo?.pickupInfo || null;
  // The waiting terms the rider is looking at; null means nothing to show or accept.
  const waitingTerms = waitingTermsFromQuote(routeInfo?.waiting);
  const tipAmount = selectedTip(pickupInfo, tipChoice);
  const payableFare = Number(routeInfo?.fare || 0) + tipAmount;
  // Pickup check: has the rider dragged the pin away from the GPS spot?
  const pickupCheckMoved = Boolean(pickupCheck && pinPlace?.lat
    && pickupCheckOutcome(distanceBetweenM(pickupCheck, pinPlace)) === 'requote');
  const pinTitle = pinTarget === 'drop'
    ? t('book.pinDropTitle')
    : (pickupCheck ? t('book.pickupCheckTitle') : t('book.pinPickupTitle'));
  const pinConfirmLabel = pinTarget === 'drop'
    ? t('book.pinConfirmDrop')
    : (pickupCheck && !pickupCheckMoved ? t('book.pickupCheckBook', { fare: payableFare }) : t('book.pinConfirmPickup'));
  const pickupMsg = pickupMessage(pickupInfo);
  const dropLabel = dropTimeLabel(pickupInfo, routeInfo?.durationMin, new Date(), lang === 'te' ? 'te-IN' : 'en-IN');

  const handleBack = () => {
    if (step === 'pin') closePinPicker();
    else if (step === 'confirm') setStep('search');
    else if (step !== 'home') goHome();
  };
  const handleBackRef = useRef(handleBack);
  handleBackRef.current = handleBack;

  useEffect(() => { stepRef.current = step; }, [step]);

  // Android hardware back → previous booking step (native MainActivity → JS bridge).
  useEffect(() => {
    return registerAndroidBackHandler(() => {
      if (stepRef.current === 'home') return false;
      handleBackRef.current();
      return true;
    });
  }, []);

  const activeTowns = getServiceZones({ activeOnly: true });
  // A town controls both pickup eligibility and fare quoting. Once a pickup is
  // set, changing town would silently discard the passenger's trip context, so
  // keep switching available only before pickup selection.
  const canSwitchTown = step === 'home' && !pickup && !drop;

  useEffect(() => {
    if (!canSwitchTown) setShowTownSwitcher(false);
  }, [canSwitchTown]);

  const waitingForRideRestore = Boolean(user?.uid && !ridesLoaded && !ridesRestoreTimedOut);

  if (!townReady) {
    // Until Book can draw, keep showing the same splash the app opened with
    // (one continuous splash, no separate "Preparing…" screen). The town
    // chooser is only for a rider with no saved town: a saved one is applied
    // by the startup effect right after this render, so showing the chooser
    // here first made it flash on every launch.
    const savedTownId = readSelectedTownId();
    const hasSavedTown = Boolean(savedTownId && activeTowns.some((zone) => zone.id === savedTownId));
    const townDataUnavailable = !platformConfigReady && !hasSavedTown
      && (platformConfigError || configWaitTimedOut) && !waitingForRideRestore && !activeRide;
    if (townDataUnavailable) {
      return (
        <div className="rapido-screen town-gate-screen">
          <section className="town-selector is-blocking" aria-label={t('book.townLoadTitle')}>
            <div className="town-selector-icon" aria-hidden>📍</div>
            <h1>{t('book.townLoadTitle')}</h1>
            <p>{t(savedTownId ? 'book.townLoadBody' : 'book.townLoadBodyNoSaved')}</p>
            <button
              type="button"
              className="town-use-location"
              disabled={townRetrying}
              onClick={() => {
                setTownRetrying(true);
                retryPlatformConfig();
              }}
            >
              {townRetrying ? t('book.townLoadRetrying') : t('book.townLoadRetry')}
            </button>
          </section>
        </div>
      );
    }
    const showChooser = platformConfigReady && !waitingForRideRestore && !activeRide && !hasSavedTown;
    if (!showChooser) return <Splash />;
    return (
      <div className="rapido-screen town-gate-screen">
        <TownSelector
          towns={activeTowns}
          selectedTownId={APP.currentZoneId}
          onSelect={chooseTown}
          onUseLocation={useCurrentLocationForTown}
          locating={locating}
          blocking
          t={t}
        />
      </div>
    );
  }

  return (
    <div className={`rapido-screen rapido-step-${step}`}>
      <div ref={mapRef} className="rapido-map" />

      {step === 'pin' && (
        <div className="rapido-center-pin" aria-hidden>
          <div className="rapido-center-pin-stem">
            <div className={`rapido-center-pin-head ${pinTarget === 'drop' ? 'drop' : 'pickup'}`} />
          </div>
          <div className="rapido-center-pin-shadow" />
        </div>
      )}

      {(step === 'home' || step === 'search') && pickup && !drop && (
        <div className="rapido-center-pin" aria-hidden>
          <div className="rapido-pin-tooltip">
            <div className="rapido-pin-tooltip-tag">
              <span className="rapido-pin-tooltip-dot" />
              <span>{t('book.yourLocation') || 'Your location'}</span>
            </div>
            <span className="rapido-pin-tooltip-addr">{truncate(pickupAwaitingAddress ? pickupLabel : (pickup?.address || pickupLabel), 34)}</span>
          </div>
          <div className="rapido-center-pin-stem">
            <div className="rapido-center-pin-head pickup">
              <div className="rapido-center-pin-dot" />
            </div>
            <div className="rapido-pin-pulse" />
          </div>
          <div className="rapido-center-pin-shadow" />
        </div>
      )}

      <div className={`rapido-map-top${step === 'home' ? ' rapido-map-top-home' : ''}`}>
        {step === 'home' ? (
          <>
            <button
              type="button"
              className="rapido-icon-btn rapido-layers-btn"
              onClick={toggleMapType}
              aria-label={t('book.toggleSatellite')}
              aria-pressed={mapType === 'hybrid'}
            >
              <LayersMapIcon />
            </button>
            {canSwitchTown ? (
              <button type="button" className="rapido-city-pill rapido-city-switcher" onClick={() => setShowTownSwitcher(true)}>
                <span className="rapido-city-dot" />
                {APP.city}
                <TownSwitcherChevron />
              </button>
            ) : (
              <span className="rapido-city-pill"><span className="rapido-city-dot" />{APP.city}</span>
            )}
            <LangToggle compact />
          </>
        ) : (
          <>
            <button type="button" className="rapido-icon-btn" onClick={handleBack} aria-label={t('common.back')}>←</button>
            <button
              type="button"
              className="rapido-icon-btn rapido-layers-btn"
              onClick={toggleMapType}
              aria-label={t('book.toggleSatellite')}
              aria-pressed={mapType === 'hybrid'}
            >
              <LayersMapIcon />
            </button>
            <span className="rapido-city-pill"><span className="rapido-city-dot" />{APP.city}</span>
            <LangToggle compact />
          </>
        )}
      </div>

      <button type="button" className={`rapido-gps-fab ${locating ? 'locating' : ''}`} onClick={recenterForPinOrPickup} aria-label={t('book.recenter')}>
        <MapRecenterIcon />
      </button>

      {mapError && <div className="rapido-map-error">{mapError}</div>}

      {canSwitchTown && showTownSwitcher && (
        <div className="town-switcher-backdrop" role="presentation" onMouseDown={() => setShowTownSwitcher(false)}>
          <div role="dialog" aria-modal="true" aria-label={t('book.switchTownTitle') || 'Switch town'} onMouseDown={(event) => event.stopPropagation()}>
            <TownSelector
              towns={activeTowns}
              selectedTownId={APP.currentZoneId}
              onSelect={chooseTown}
              onUseLocation={useCurrentLocationForTown}
              locating={locating}
              onClose={() => setShowTownSwitcher(false)}
              t={t}
            />
          </div>
        </div>
      )}

      <div ref={sheetRef} className={sheetClass}>
        {step === 'pin' && (
          <>
            <div className="rapido-sheet-handle" aria-hidden />
            <h2 className="rapido-sheet-title">{pinTitle}</h2>
            <p className={`rapido-pin-hint${dropRedirect?.ok ? ' rapido-pin-redirect' : ''}`}>
              {dropRedirect?.ok
                ? t('book.intertownRedirectHint')
                : (pinTarget === 'drop' && pinAreaName
                  ? t('book.pinAreaHint', { name: pinAreaName })
                  : pickupCheck
                  ? t(pickupCheckMoved ? 'book.pickupCheckMovedHint' : 'book.pickupCheckHint')
                  : t('book.pinHint'))}
            </p>
            <div className={`rapido-pin-addr-card ${!pinValid ? 'invalid' : ''}`}>
              <span className={`rapido-loc-icon ${pinTarget === 'drop' ? 'drop' : 'pickup'}`} />
              <div className="rapido-pin-addr-text">
                <span className="rapido-pin-addr-name">{pinName}</span>
                {pinAddr && <span className="rapido-pin-addr-sub">{pinAddr}</span>}
              </div>
            </div>
            <PinSheetAction
              t={t}
              redirect={dropRedirect}
              redirecting={redirecting}
              onRedirect={goToIntertown}
              confirmLabel={pinConfirmLabel}
              confirmDisabled={!pinPlace || !pinValid}
              onConfirm={confirmPin}
            />
          </>
        )}

        {step === 'home' && (
          <>
            <div className="rapido-sheet-handle" aria-hidden />
            <div className="rapido-home-greeting">
              <h1 className="rapido-home-hi">
                <span>{getTimeGreeting(t)} </span>
                <span className="rapido-home-hi-name">{userProfile?.name?.split(' ')[0] || (user?.phoneNumber ? `${t('book.riderFallback')} ${user.phoneNumber.slice(-4)}` : t('book.riderThere'))}!</span>
              </h1>
              <p className="rapido-home-sub">{t('book.whereHeaded') || 'Where are you headed?'}</p>
            </div>

            {bookingHalted && (
              <div
                className={`rapido-status-card ${platformMode === 'emergency' ? 'is-emergency' : ''}`}
                role="status"
              >
                <span className="rapido-status-card-icon" aria-hidden>
                  {platformMode === 'emergency' ? '⛔' : '⏸️'}
                </span>
                <div className="rapido-status-card-body">
                  <strong>
                    {platformMode === 'emergency'
                      ? (t('book.stoppedTitle') || 'Bookings unavailable')
                      : (t('book.pausedTitle') || 'Bookings paused')}
                  </strong>
                  <p>
                    {(platformMessage && platformMessage.trim())
                      || (platformMode === 'emergency'
                        ? (t('book.stoppedBody') || 'MyTownly is temporarily unavailable. Please try again shortly.')
                        : (t('book.pausedBody') || 'New bookings are paused right now. Please check back soon.'))}
                  </p>
                </div>
              </div>
            )}

            <button
              type="button"
              className="rapido-where-to-btn rapido-where-to-hero"
              data-tour="where"
              onClick={openSearch}
              disabled={bookingHalted || (locating && !pickup && !userGps)}
            >
              <div className="rapido-search-badge" aria-hidden>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
              </div>
              <div className="rapido-where-to-content">
                <span className="rapido-where-to-title">
                  {!pickupValid || !pickup ? t('book.setPickupFirst') : (t('book.whereTo') || 'Where to?')}
                </span>
              </div>
            </button>

            <EmergencyContactsPrompt
              uid={user?.uid}
              t={t}
              contacts={userProfile?.emergencyContacts}
              hidden={!notifReady || notifSheetActive || Boolean(forOtherSheet) || showTour}
            />

            <div className="rapido-chips-section">
              <div className="rapido-chips-label">{t('book.quickDestinations') || 'Quick Destinations'}</div>
              <div className="quick-shortcuts-row" data-tour="shortcuts" aria-label={t('book.quickDestinations')}>
                <button type="button" className="shortcut-pill" onClick={() => handleShortcutClick({ id: 'home' })}>
                  <span className="shortcut-icon"><HomeChipIcon /></span>
                  <span>{t('places.home') || 'Home'}</span>
                </button>
                <button type="button" className="shortcut-pill" onClick={() => handleShortcutClick({ id: 'work' })}>
                  <span className="shortcut-icon"><WorkChipIcon /></span>
                  <span>{t('places.work') || 'Work'}</span>
                </button>
                {/* Preset places are fixed points (Piler's today): show only the
                    ones inside the town on screen, so a rider in another town is
                    never offered a drop 15 km away. Re-checked on every town change. */}
                {PRESET_SHORTCUTS.filter((s) => isWithinServiceArea(s.place.lat, s.place.lng)).map((s) => (
                  <button key={s.id} type="button" className="shortcut-pill" onClick={() => handleShortcutClick(s)}>
                    <span className="shortcut-icon"><s.Icon /></span>
                    <span>{t(`book.${s.key}`) || s.label}</span>
                  </button>
                ))}
                <button type="button" className="shortcut-pill shortcut-add" onClick={() => navigate('/profile')}>
                  <span className="shortcut-icon"><PlusChipIcon /></span>
                  <span>{t('common.add') || 'Add'}</span>
                </button>
              </div>
            </div>

            <div
              className="rapido-home-pickup"
              data-tour="pickup"
              onClick={() => { setStep('search'); setEditingPickup(true); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { setStep('search'); setEditingPickup(true); } }}
              role="button"
              tabIndex={0}
            >
              <div className="rapido-home-pulse-dot" aria-hidden>
                <span className="pulse-ring" />
                <span className="pulse-core" />
              </div>
              <div className="rapido-home-pickup-text">
                <span className="rapido-home-label">{t('book.pickupFrom') || 'PICKUP FROM'}</span>
                <span className={`rapido-home-addr${locating ? ' locating' : ''}`}>{pickupLabel}</span>
              </div>
              <span className="rapido-home-edit-btn" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
              </span>
            </div>
            {/* Right under the pickup it may be about: the notes at the end of
                the sheet are below the fold. */}
            {startupLocationOff && (
              <p className="rapido-area-note rapido-area-warn">{t('book.locationServicesOff')}</p>
            )}

            {recentPlaces.length > 0 && pickupValid && pickup && (
              <div className="rapido-recent">
                <div className="rapido-recent-header">
                  <h3 className="rapido-recent-title">{t('book.recent') || 'Recent places'}</h3>
                  <button type="button" className="rapido-recent-see-all" onClick={openSearch}>
                    {t('common.seeAll') || 'See all'}
                  </button>
                </div>
                <div className="rapido-recent-list">
                  {recentPlaces.slice(0, 3).map((place) => (
                    <div
                      key={`${place.lat}-${place.lng}`}
                      className="rapido-recent-item"
                      onClick={() => selectRecent(place)}
                      role="button"
                      tabIndex={0}
                    >
                      <div className="rapido-recent-ico-circle">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"/>
                          <polyline points="12 6 12 12 16 14"/>
                        </svg>
                      </div>
                      <div className="rapido-recent-text">
                        <span className="rapido-recent-name">{place.name || place.address}</span>
                        {place.address && place.name !== place.address && (
                          <span className="rapido-recent-sub">{truncate(place.address, 38)}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="rapido-home-trust-pills">
              <span className="trust-pill">✓ {t('book.trustOtp') || 'OTP verified'}</span>
              <span className="trust-pill">💵 {t('book.trustCash') || 'Cash on drop'}</span>
              <span className="trust-pill">📍 {APP.serviceRadiusKm} km {t('book.serviceRadius') || 'service area'}</span>
            </div>

            {!pickupValid && pickup && (
              <p className="rapido-area-note rapido-area-warn">{t('book.outsideArea', { km: APP.serviceRadiusKm, city: APP.city })}</p>
            )}
            {locationError && !pickup && (
              <p className="rapido-area-note rapido-area-warn">{locationError}</p>
            )}

            {pickupValid && pickup?.isApproximate && (
              <p className="rapido-area-note rapido-area-warn">{t('book.locationApproxHint')}</p>
            )}
            {pickupValid && !pickup?.isApproximate && (
              <p className="rapido-area-note">{t('book.serviceArea', { city: APP.city, km: APP.serviceRadiusKm })}</p>
            )}
          </>
        )}

        {step === 'search' && (
          <>
            <div className="rapido-sheet-handle" aria-hidden />
            <h2 className="rapido-sheet-title">{t('book.planTrip')}</h2>

            <div className="rapido-loc-stack">
              <div className="rapido-loc-field">
                <div className="rapido-loc-field-head">
                  <span className="rapido-loc-icon pickup" />
                  <span className="rapido-loc-label">{t('book.pickup')}</span>
                </div>
                {editingPickup ? (
                  <div className="rapido-loc-input-box focus">
                    <div ref={pickupAcRef} className="place-ac-wrap rapido-ac" />
                  </div>
                ) : (
                  <button type="button" className="rapido-loc-input-box" onClick={() => setEditingPickup(true)}>
                    <div className="rapido-loc-input-text">
                      <span className="rapido-loc-input-title">{pickupTitle}</span>
                      {pickupSub && <span className="rapido-loc-input-sub">{pickupSub}</span>}
                    </div>
                    {!locating && <span className="rapido-loc-change">{t('book.change')}</span>}
                  </button>
                )}
                <button type="button" className="rapido-map-pin-link" onClick={() => openPinPicker('pickup')}>
                  <span className="rapido-map-pin-ico" aria-hidden>📍</span>
                  {t('book.locateOnMap')}
                </button>
              </div>

              <div className="rapido-loc-connector" aria-hidden />

              <div className="rapido-loc-field">
                <div className="rapido-loc-field-head">
                  <span className="rapido-loc-icon drop" />
                  <span className="rapido-loc-label">{t('book.drop')}</span>
                </div>
                <div className="rapido-loc-input-box focus">
                  <div ref={dropAcRef} className="place-ac-wrap rapido-ac" />
                </div>
                <button type="button" className="rapido-map-pin-link" onClick={() => openPinPicker('drop')}>
                  <span className="rapido-map-pin-ico" aria-hidden>📍</span>
                  {t('book.locateOnMap')}
                </button>

                {savedDestinationShortcuts.length > 0 && (
                  <div className="rapido-saved-places" aria-label={t('places.savedLocations') || 'Saved places'}>
                    <div className="rapido-saved-places-title">
                      {t('places.savedLocations') || 'Saved places'}
                    </div>
                    <div className="rapido-saved-places-row">
                      {savedDestinationShortcuts.map(({ id, label, place, Icon }) => (
                        <button
                          key={id}
                          type="button"
                          className="rapido-saved-place-card"
                          onClick={() => handleShortcutClick({ id })}
                          aria-label={`${label}${place.address ? `, ${place.address}` : ''}`}
                        >
                          <span className="rapido-saved-place-icon" aria-hidden><Icon /></span>
                          <span className="rapido-saved-place-copy">
                            <span className="rapido-saved-place-name">{label}</span>
                            <span className="rapido-saved-place-address">{truncate(place.address || place.name, 26)}</span>
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {recentPlaces.length > 0 && (
              <div className="rapido-recent rapido-recent-search">
                <div className="rapido-recent-title">{t('book.recent')}</div>
                {recentPlaces.map((place) => (
                  <button
                    key={`s-${place.lat}-${place.lng}`}
                    type="button"
                    className="rapido-recent-item"
                    onClick={() => selectRecent(place)}
                  >
                    <span className="rapido-recent-ico">◷</span>
                    <span className="rapido-recent-name">{truncate(place.name, 38)}</span>
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        {step === 'confirm' && (
          <>
            <div
              className="rapido-sheet-handle-zone"
              onClick={() => setConfirmMinimized((v) => !v)}
              onTouchStart={handleSheetTouchStart}
              onTouchEnd={handleSheetTouchEnd}
              role="button"
              tabIndex={0}
              aria-label={confirmMinimized ? (t('book.showDetails') || 'Show details') : (t('book.showMap') || 'Collapse to map')}
            >
              <div className="rapido-sheet-handle" aria-hidden />
            </div>

            {confirmMinimized ? (
              <div
                className="rapido-confirm-peek"
                onClick={() => setConfirmMinimized(false)}
                onTouchStart={handleSheetTouchStart}
                onTouchEnd={handleSheetTouchEnd}
                role="button"
                tabIndex={0}
              >
                <div className="rapido-confirm-peek-info">
                  <span className="rapido-confirm-peek-emoji">🛺</span>
                  <div className="rapido-confirm-peek-text">
                    <strong className="rapido-confirm-peek-fare">
                      {routeInfo?.isServerQuote ? `₹${payableFare}` : (t('book.calculating') || 'Calculating fare…')}
                    </strong>
                    <span className="rapido-confirm-peek-meta">
                      {routeInfo?.isServerQuote && routeInfo.distanceKm
                        ? `${routeInfo.distanceKm.toFixed(1)} km • ${routeInfo.durationMin || '5'} min`
                        : (t('book.calculating') || 'Calculating fare…')}
                    </span>
                  </div>
                </div>
                <div className="rapido-confirm-peek-arrow" aria-hidden="true">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="18 15 12 9 6 15" />
                  </svg>
                </div>
              </div>
            ) : (
              <div
                className="rapido-confirm-body"
                onTouchStart={handleSheetTouchStart}
                onTouchEnd={handleSheetTouchEnd}
              >
                <h2 className="rapido-sheet-title">{t('book.confirmRide')}</h2>
                <div className="rapido-trip-card">
                  <div className="rapido-trip-card-row">
                    <span className="rapido-loc-icon pickup" />
                    <div className="rapido-trip-card-copy">
                      <div className="rapido-loc-label">{t('book.pickup')}</div>
                      {pickup?.lat ? (
                        <div className="rapido-trip-addr">{formatPlaceLabel(pickup, 52) || truncate(pickup?.address || pickup?.name, 52)}</div>
                      ) : (
                        <button type="button" className="rapido-trip-addr missing" style={{ color: '#e53e3e', fontWeight: 700, background: 'none', border: 'none', textAlign: 'left', padding: 0, cursor: 'pointer' }} onClick={() => { setStep('search'); setEditingPickup(true); }}>
                          ⚠️ {t('book.tapToSetPickup') || 'Tap to set pickup location'}
                        </button>
                      )}
                      <button type="button" className="rapido-adjust-map" onClick={() => openPinPicker('pickup')}>
                        {t('book.adjustOnMap')}
                      </button>
                    </div>
                  </div>
                  <div className="rapido-trip-card-row is-drop">
                    {/* Sits on the divider between the two stops (index.css). */}
                    <button type="button" className="rapido-swap-btn" onClick={swapLocations} title={t('book.swap')} aria-label={t('book.swap')}>⇅</button>
                    <span className="rapido-loc-icon drop" />
                    <div className="rapido-trip-card-copy">
                      <div className="rapido-loc-label">{t('book.drop')}</div>
                      <div className="rapido-trip-addr">{formatPlaceLabel(drop, 52) || truncate(drop?.name || drop?.address, 52)}</div>
                      <button type="button" className="rapido-adjust-map" onClick={() => openPinPicker('drop')}>
                        {t('book.adjustOnMap')}
                      </button>
                    </div>
                  </div>
                  <button type="button" className="rapido-edit-trip" onClick={() => setStep('search')}>{t('book.editTrip')}</button>
                </div>

                {movedFromFare && routeInfo?.isServerQuote && !routeLoading
                  && movedFromFare.routeKey === routePointsKey
                  && lastFetchedRouteKeyRef.current === routePointsKey && (
                  <p className="rapido-pickup-moved-note" role="status">
                    {Number(routeInfo.fare) === movedFromFare.fare
                      ? t('book.pickupMovedSameFare')
                      : t('book.pickupMovedFare', { from: movedFromFare.fare, to: Number(routeInfo.fare) })}
                  </p>
                )}

                <button
                  type="button"
                  className={`rapido-note-row${riderNote ? ' has-note' : ''}`}
                  onClick={() => setNoteSheetOpen(true)}
                >
                  <span className="rapido-note-row-ico" aria-hidden>📝</span>
                  <span className="rapido-note-row-text">
                    <strong>{riderNote ? t('riderNote.title') : t('riderNote.add')}</strong>
                    <span>{riderNote ? riderNoteSummary(riderNote, t) : t('riderNote.addHint')}</span>
                  </span>
                  <span className="rapido-note-row-action">{riderNote ? t('riderNote.edit') : t('riderNote.addShort')}</span>
                </button>

                {routeLoading && <p className="rapido-loading-route">{t('book.calculating')}</p>}

                {routeInfo && !routeLoading && (
                  <>
                    <div className="rapido-vehicle-row">
                      <div className="rapido-vehicle selected">
                        <span className="rapido-vehicle-emoji">🛺</span>
                        <div>
                          <div className="rapido-vehicle-name">{t('book.auto')}</div>
                          <div className="rapido-vehicle-meta">
                            {t('book.distance', { km: routeInfo.distanceKm.toFixed(1), min: routeInfo.durationMin })}
                          </div>
                        </div>
                        <div className="rapido-vehicle-fare">₹{payableFare}</div>
                      </div>
                    </div>

                    {pickupInfo && (
                      <div className="rapido-vehicle-sub">
                        {pickupInfo.level !== 'none' && pickupInfo.etaMin != null && (
                          <span>{t('book.pickupEta', { min: pickupInfo.etaMin })}</span>
                        )}
                        {dropLabel && <span>{t('book.dropAt', { time: dropLabel })}</span>}
                        <span className="rapido-vehicle-seats" aria-label={t('book.seatsLabel', { n: AUTO_SEATS })}>👤 {AUTO_SEATS}</span>
                      </div>
                    )}

                    {pickupMsg && (pickupInfo.tipAmounts?.length > 0 || pickupInfo.level === 'none') && (
                      <div className="rapido-boost-container rapido-pickup-tip">
                        <div className="rapido-boost-header">
                          <span className="rapido-boost-title">{t(pickupMsg.headline, pickupMsg.vars)}</span>
                          {tipAmount > 0 && (
                            <span className="rapido-boost-applied">{t('book.pickupTipChosen', { amount: tipAmount })}</span>
                          )}
                        </div>
                        <p className="rapido-boost-hint">{t(pickupMsg.hint)}</p>
                        {pickupInfo.tipAmounts?.length > 0 && (
                        <div className="rapido-boost-chips" role="group" aria-label={t('fare.pickupTip')}>
                          {pickupInfo.tipAmounts.map((amt) => (
                            <button
                              key={amt}
                              type="button"
                              className={`rapido-boost-chip ${tipAmount === amt ? 'selected' : ''}`}
                              aria-pressed={tipAmount === amt}
                              disabled={booking}
                              onClick={() => { hapticSelection(); setTipChoice(tipAmount === amt ? 0 : amt); }}
                            >
                              +₹{amt}
                            </button>
                          ))}
                        </div>
                        )}
                      </div>
                    )}

                    <div className="rapido-fare-breakdown">
                      <div className="rapido-fare-row">
                        <span>{t('book.fareBase')}</span>
                        <span>₹{fareDisplay.base}</span>
                      </div>
                      <div className="rapido-fare-row">
                        <span>{t('book.fareDistance', { km: Number(fareDisplay.distanceKm || 0).toFixed(1), rate: fareDisplay.perKm })}</span>
                        <span>₹{fareDisplay.distanceCharge}</span>
                      </div>
                      {fareDisplay.appliedMinimum && (
                        <div className="rapido-fare-row">
                          <span>{t('book.fareMinimum')}</span>
                          <span>₹{fareDisplay.minimum}</span>
                        </div>
                      )}
                      {fareDisplay.isNightFare && fareDisplay.nightSurcharge > 0 && (
                        <div className="rapido-fare-row font-medium" style={{ color: '#4f46e5' }}>
                          <span>🌙 {t('fare.nightSurcharge') || 'Night surcharge'}</span>
                          <span>+₹{fareDisplay.nightSurcharge}</span>
                        </div>
                      )}
                      {fareDisplay.isRainFare && fareDisplay.rainSurcharge > 0 && (
                        <div className="rapido-fare-row font-medium" style={{ color: '#0369a1' }}>
                          <span>🌧️ {t('fare.rainSurcharge') || 'Rain surcharge'}</span>
                          <span>+₹{fareDisplay.rainSurcharge}</span>
                        </div>
                      )}
                      {fareDisplay.cancellationFee > 0 && (
                        <div className="rapido-fare-row text-amber-700 font-medium" style={{ color: '#b45309' }}>
                          <span>{t('fare.cancellationFee') || 'Previous cancellation fee'}</span>
                          <span>+₹{fareDisplay.cancellationFee}</span>
                        </div>
                      )}
                      {tipAmount > 0 && (
                        <div className="rapido-fare-row font-medium" style={{ color: '#b45309' }}>
                          <span>{t('fare.pickupTip')}</span>
                          <span>+₹{tipAmount}</span>
                        </div>
                      )}
                      <div className="rapido-fare-row total">
                        <span>{t('book.estimatedFare')}</span>
                        <strong>₹{payableFare}</strong>
                      </div>
                    </div>

                    <p className="rapido-cash-note">
                      💵 {t('pay.cashHint')}
                    </p>
                  </>
                )}

                {forOtherDecision === 'other' && riderDetails && (
                  <div className="bfse-confirm-chip">
                    <strong>{t('book.ridingFor', { name: riderDetails.riderName })}</strong>
                    <span>+91 {riderDetails.riderPhone}</span>
                    <button type="button" className="bfse-chip-edit" onClick={() => setForOtherSheet({ reason: 'farPickup', distanceKm: evalBookForOther(userGps, pickup).distanceKm })}>
                      {t('book.change')}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Always visible (also when the sheet is collapsed): tapping Book accepts exactly these terms. */}
            {waitingTerms && routeInfo?.isServerQuote && (
              <p className="rapido-waiting-terms">
                ⏱ {t('book.waitingTerms', { free: waitingTerms.freeMinutes, rate: waitingTerms.perMinute })}
              </p>
            )}

            <button
              type="button"
              className={`rapido-book-btn ${booking ? 'loading' : ''}`}
              data-tour="book"
              disabled={!online || !routeInfo?.isServerQuote || booking || !pickupValid || !pickup?.lat || !drop?.lat || !!activeRide || !!activeIntertownRequest}
              onClick={() => handleBook()}
            >
              {booking ? (
                <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
                  <span style={{ width: 18, height: 18, border: '2.5px solid rgba(255,255,255,0.35)', borderTopColor: '#ffffff', borderRadius: '50%', animation: 'spin 0.7s linear infinite', display: 'inline-block' }} aria-hidden />
                  <span>{t('book.searching') || 'Finding Captain...'}</span>
                </div>
              ) : activeIntertownRequest ? (
                t('book.finishIntertownFirst')
              ) : (
                routeInfo?.isServerQuote
                  ? t('book.bookAutoFor', { fare: payableFare })
                  : (t('book.calculating') || 'Calculating fare…')
              )}
            </button>
          </>
        )}
      </div>

      {showTour && user?.uid && (
        <FirstTimeTour
          t={t}
          uid={user.uid}
          onDone={() => setShowTour(false)}
        />
      )}

      {/* Outside sheet stacking context so overlay covers bottom nav fully */}
      {step === 'home' && (
        <NotificationPrompt
          uid={user?.uid}
          t={t}
          blocked={showTour}
          onActiveChange={setNotifSheetActive}
          onReady={() => setNotifReady(true)}
        />
      )}

      {noteSheetOpen && (
        <RiderNoteSheet
          t={t}
          note={riderNote}
          onClose={() => setNoteSheetOpen(false)}
          onSave={(note) => { setRiderNote(note); setNoteSheetOpen(false); }}
        />
      )}

      <BookForSomeoneSheet
        open={Boolean(forOtherSheet) && !showTour && (
          step !== 'home' || (notifReady && !notifSheetActive)
        )}
        t={t}
        reason={forOtherSheet?.reason}
        distanceKm={forOtherSheet?.distanceKm}
        onClose={() => {
          const early = forOtherSheet?.early;
          setForOtherSheet(null);
          if (early) {
            markOutOfTownAsked();
            guideSetPickupInTown();
          }
        }}
        onForSelf={() => {
          const early = forOtherSheet?.early;
          setForOtherDecision('self');
          setRiderDetails(null);
          setForOtherSheet(null);
          if (early) {
            markOutOfTownAsked();
            guideSetPickupInTown();
          }
        }}
        onForOther={(details) => {
          const early = forOtherSheet?.early;
          setForOtherDecision('other');
          setRiderDetails(details);
          setForOtherSheet(null);
          toast.success(t('book.riderSaved', { name: details.riderName }));
          if (early) markOutOfTownAsked();
          guideSetPickupInTown();
        }}
      />

      {booking && (
        <div className="rapido-booking-radar-overlay" aria-live="assertive">
          <div className="rapido-radar-glow-ring r1" />
          <div className="rapido-radar-glow-ring r2" />
          <div className="rapido-radar-glow-ring r3" />
          <div className="rapido-radar-center-card">
            <div className="rapido-radar-auto-icon">🛺</div>
            <h3 className="rapido-radar-title">{t('book.searching') || 'Connecting to nearby Annas...'}</h3>
            <p className="rapido-radar-sub">{pickup?.address || pickup?.name || APP.city}</p>
            <div className="rapido-radar-dots">
              <span className="dot d1" />
              <span className="dot d2" />
              <span className="dot d3" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
